/**
 * Crew Feed card contract + pulse_cards adapter (F0)
 *
 * Shared by SpacePulse Go resolution and Your crew edition.
 * Improve here — do not fork card normalization in UI components.
 *
 * Primary responsibilities:
 * - Map pulse card_type → lane / mate
 * - Derive why + primary_cta (mandatory Go)
 * - Adapt action_data via pulse-card-target (resolvePulseGoHref / getPulseTarget)
 * - Drop cards with no resolvable Go (normalizePulseRow → null),
 *   except propose_mutate Ask CTAs (href optional; payload = draft)
 *
 * Dependencies:
 * - `@/lib/crew/pulse-card-target` for Go href nucleus
 *
 * Compat / schema gap:
 * - Additive columns live in
 *   `supabase/migrations/20260930160000_pulse_cards_crew_feed_columns.sql`
 *   (`lane`, `why`, `sources`, `primary_cta`, `mate_id` — all nullable).
 * - Writers soft-populate via `buildCrewFeedColumnWrites` +
 *   `insertPulseCardsWithOptionalFeedColumns` (try columns; strip+retry if
 *   migration not applied). Reader prefers stored columns, else
 *   adapter fallback (§8.6): lane←card_type, why←deriveWhy, sources←
 *   source_data.sources/crumbs, primary_cta←action_data via resolvePulseGoHref.
 * - Prod stays safe before/after migrate: missing columns ≡ adapter-only path.
 *
 * Mobile parity (Metro cannot import this module):
 * - View-model types: `mobile/lib/crew/feed-card-types.ts`
 * - Thin adapter: `mobile/lib/crew/normalize.ts` + `pulse-go.ts`
 * - API client: `mobile/lib/crew/feed-api.ts` → same `/api/pulse/*` routes
 * - RN UI: `mobile/components/pulse/CrewFeedCard.tsx`
 * Keep CrewFeedCard fields + lane/mate maps in lockstep when changing here.
 * Prefer storing primary_cta.href when writers already resolved Go; mobile
 * pulse-go also thin-resolves Space-item shapes when href is absent.
 */

import { z } from 'zod'

import {
  mateForLane as mateForLaneFromCatalog,
  resolveFeedMate,
  type CrewFeedMateId
} from '@/lib/crew/mates'
import {
  buildChatQueryHref,
  buildCrmCompanyHref,
  buildCrmContactHref,
  buildCrmLeadHref,
  buildSpaceItemHref,
  buildStandaloneSpaceItemHref,
  getJsonRecord,
  getRecordString,
  isLivePulseChatHref,
  resolvePulseGoHref,
  rewriteAbsentCrmLeadDetailHref,
  SPACE_ITEM_TYPES,
  CRM_LEAD_DETAIL_ROUTE_EXISTS,
  CRM_LEAD_ID_KEYS,
  CRM_LEAD_LIST_DEEPLINK_EXISTS,
  type PulseJsonRecord,
  type SpaceLinkedItemType
} from '@/lib/crew/pulse-card-target'

/**
 * Discuss CTA seed mode — always solo_seed (Feed Discuss ≠ Crew Chat).
 * Crew Chat uses CREW_CHAT_SEED_MODE = multi_mate separately.
 */
export const CREW_FEED_DISCUSS_SEED_MODE = 'solo_seed' as const

/**
 * Re-export Go nucleus — callers may import from feed-card or pulse-card-target.
 * Keep lockstep with mobile `lib/crew` barrel (SPACE_ITEM + CRM lead honesty).
 */
export {
  buildCrmLeadHref,
  buildSpaceItemHref,
  buildStandaloneSpaceItemHref,
  CRM_LEAD_DETAIL_ROUTE_EXISTS,
  CRM_LEAD_ID_KEYS,
  CRM_LEAD_LIST_DEEPLINK_EXISTS,
  isLivePulseChatHref,
  resolvePulseGoHref,
  rewriteAbsentCrmLeadDetailHref,
  SPACE_ITEM_TYPES,
  type SpaceLinkedItemType
}

export type CrewFeedLane =
  | 'interest'
  | 'ops'
  | 'follow_up'
  | 'calendar'
  | 'insight'

/** Ordered lane catalogue — mirrors pulse_cards.lane check (§9). */
export const CREW_FEED_LANES = [
  'interest',
  'ops',
  'follow_up',
  'calendar',
  'insight'
] as const satisfies readonly CrewFeedLane[]

/**
 * Searchable path of the additive pulse_cards Crew Feed columns migration.
 * Bun contract: `__tests__/lib/crew-pulse-cards-crew-feed-columns-migration-contract.test.ts`.
 * Do not invent a second store — extend pulse_cards only (§7 locked decision).
 */
export const CREW_PULSE_CARDS_FEED_COLUMNS_MIGRATION =
  'supabase/migrations/20260930160000_pulse_cards_crew_feed_columns.sql' as const

/**
 * Nullable pulse_cards columns materialized by CREW_PULSE_CARDS_FEED_COLUMNS_MIGRATION.
 * Writers soft-populate via buildCrewFeedColumnWrites when insert succeeds;
 * if columns are absent (pre-migrate), insert strips them and retries.
 * Until then normalizePulseRow adapts from card_type / action_data /
 * source_data — never require these on the wire.
 */
export const CREW_PULSE_CARDS_FEED_COLUMN_NAMES = [
  'lane',
  'why',
  'sources',
  'primary_cta',
  'mate_id'
] as const

export type CrewPulseCardsFeedColumnName =
  (typeof CREW_PULSE_CARDS_FEED_COLUMN_NAMES)[number]

/**
 * Shared Love / Discuss / Go / Unlove chrome labels — card UI, CTA repair, home teasers.
 * Mobile mirrors these in `mobile/lib/crew/feed-card-types.ts`.
 */
export const CREW_FEED_ACTION_LOVE = 'Save' as const
export const CREW_FEED_ACTION_UNLOVE = 'Unsave' as const
export const CREW_FEED_ACTION_DISCUSS = 'Chat' as const
export const CREW_FEED_ACTION_GO = 'Open' as const

/**
 * Action-specific Go CTA labels — prefer over bare CREW_FEED_ACTION_GO when
 * the action type is known. Mobile mirrors in feed-card-types / normalize.
 */
export const CREW_FEED_GO_LABEL_CONTINUE = 'Continue' as const
export const CREW_FEED_GO_LABEL_START = 'Start' as const
export const CREW_FEED_GO_LABEL_SEARCH = 'Search' as const
export const CREW_FEED_GO_LABEL_OPEN_LEAD = 'Lead' as const
export const CREW_FEED_GO_LABEL_OPEN_COMPANY = 'Company' as const
export const CREW_FEED_GO_LABEL_OPEN_CONTACT = 'Contact' as const
export const CREW_FEED_GO_LABEL_OPEN_PLANNER = 'Planner' as const
/** Deprecated ops execute theater — silenced (anti-slop). Prefer CREW_FEED_ACTION_GO. */
export const CREW_FEED_GO_LABEL_DO_IT = '' as const

/**
 * Closed set of Go CTA labels mirrored on mobile feed-card-types.
 * Prefer named CREW_FEED_GO_LABEL_* at call sites; use this object for
 * parity iteration / drift audits only (not a second copy source).
 * Metro cannot import web/ — mobile mirrors named exports only (no
 * CREW_FEED_GO_LABEL_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-discuss-chrome-parity.test.ts`.
 */
export const CREW_FEED_GO_LABEL_MIRROR = {
  CREW_FEED_GO_LABEL_CONTINUE,
  CREW_FEED_GO_LABEL_START,
  CREW_FEED_GO_LABEL_SEARCH,
  CREW_FEED_GO_LABEL_OPEN_LEAD,
  CREW_FEED_GO_LABEL_OPEN_COMPANY,
  CREW_FEED_GO_LABEL_OPEN_CONTACT,
  CREW_FEED_GO_LABEL_OPEN_PLANNER,
  CREW_FEED_GO_LABEL_DO_IT
} as const

/**
 * Go CTA icon map keys — prefer over re-inlining Icon* strings in
 * ctaIconForLane / crewFeedGoIconKey. Mobile mirrors in feed-card-types.
 */
export const CREW_FEED_GO_ICON_SEARCH = 'IconSearch' as const
export const CREW_FEED_GO_ICON_USER = 'IconUser' as const
export const CREW_FEED_GO_ICON_PLANNER = 'IconLayoutDashboard' as const
export const CREW_FEED_GO_ICON_OPS = 'IconCheckbox' as const
export const CREW_FEED_GO_ICON_FOLLOW_UP = 'IconMessageCircle' as const
export const CREW_FEED_GO_ICON_DEFAULT = 'IconArrowUpRight' as const
export const CREW_FEED_GO_ICON_DISCUSS = 'IconMessagePlus' as const

/**
 * Closed set of Go CTA icon keys mirrored on mobile feed-card-types.
 * Prefer named CREW_FEED_GO_ICON_* at call sites; use this object for
 * parity iteration / drift audits only (not a second copy source).
 * Metro cannot import web/ — mobile mirrors named exports only (no
 * CREW_FEED_GO_ICON_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-discuss-chrome-parity.test.ts`.
 */
export const CREW_FEED_GO_ICON_MIRROR = {
  CREW_FEED_GO_ICON_SEARCH,
  CREW_FEED_GO_ICON_USER,
  CREW_FEED_GO_ICON_PLANNER,
  CREW_FEED_GO_ICON_OPS,
  CREW_FEED_GO_ICON_FOLLOW_UP,
  CREW_FEED_GO_ICON_DEFAULT,
  CREW_FEED_GO_ICON_DISCUSS
} as const

/**
 * Minimum Love / Discuss / Go control height (px) — Apple HIG ~44pt hit target.
 * Web + mobile CrewFeedCard lockstep. Prefer over forking `h-9` / `minHeight: 36`.
 */
export const CREW_FEED_ACTION_MIN_HEIGHT_PX = 44 as const

/**
 * Shared HIG hit-target style — prefer over re-inlining
 * `{ minHeight: CREW_FEED_ACTION_MIN_HEIGHT_PX }` on Feed CTAs, Scheduled,
 * InterestBrief, MeetYourCrew, ChatComposer, and Retry.
 * Mobile mirrors in feed-card-types (Metro cannot import web/).
 */
export const CREW_FEED_ACTION_HIT_STYLE = {
  minHeight: CREW_FEED_ACTION_MIN_HEIGHT_PX,
  minWidth: CREW_FEED_ACTION_MIN_HEIGHT_PX,
  height: 'auto'
} as const

/**
 * Shared Retry button pad (CrewFeedList error panel). Pair with
 * CREW_FEED_ACTION_HIT_STYLE — prefer over re-inlining px-3.
 */
export const CREW_FEED_RETRY_BUTTON_CLASSNAME = 'px-3' as const

/**
 * Shared Feed action button className — Love / Discuss (Go adds ml-auto
 * host wrap). Prefer over re-inlining min-w/gap/px/touch thrash. Pair with
 * CREW_FEED_ACTION_HIT_STYLE for HIG 44pt height.
 */
export const CREW_FEED_ACTION_BUTTON_CLASSNAME =
  'h-auto min-w-[44px] gap-1.5 px-2.5 touch-manipulation' as const

/**
 * Visible Love / Discuss action label (web Card span). Mobile peer is
 * styles.actionLabel fontSize 12 — document only; do not fork text-xs.
 */
export const CREW_FEED_ACTION_LABEL_CLASSNAME = 'text-xs' as const

/**
 * Visible Go action label (web Card span). Slightly stronger weight than
 * Love/Discuss. Mobile peer is styles.goLabel fontWeight 600.
 */
export const CREW_FEED_ACTION_GO_LABEL_CLASSNAME =
  'text-xs font-medium' as const

/**
 * Shared Crew surface panel chrome — InterestBrief / ScheduledList /
 * empty-edition teasers. Prefer over re-inlining rounded-2xl border thrash.
 */
export const CREW_SURFACE_PANEL_CLASSNAME =
  'rounded-2xl border border-border/60 bg-background/70 backdrop-blur-sm' as const

/**
 * Shared panel inner pad (InterestBrief / ScheduledList). Prefer over
 * re-inlining px-4 py-4 thrash.
 */
export const CREW_SURFACE_PANEL_PAD_CLASSNAME = 'px-4 py-4' as const

/**
 * Shared panel body stack + responsive pad (Member chrome). Prefer over
 * re-inlining flex flex-col gap-3 p-4 md:p-5 thrash.
 */
export const CREW_SURFACE_PANEL_STACK_CLASSNAME =
  'flex flex-col gap-3 p-4 md:p-5' as const

/**
 * Shared looser panel body stack (MeetYourCrew). Prefer over re-inlining
 * flex flex-col gap-4 p-4 md:p-5 thrash.
 */
export const CREW_SURFACE_PANEL_STACK_LOOSE_CLASSNAME =
  'flex flex-col gap-4 p-4 md:p-5' as const

/**
 * Shared Crew surface card chrome — live CrewFeedCard article shell
 * (slightly stronger fill than panel). Prefer over re-inlining.
 */
export const CREW_SURFACE_CARD_CLASSNAME =
  'rounded-2xl border border-border/60 bg-background/80 backdrop-blur-sm' as const

/**
 * Shared Crew feed sources nest shell — CrewFeedSources scroll list.
 * Prefer over re-inlining the muted inset chrome.
 */
export const CREW_SURFACE_SOURCES_CLASSNAME =
  'flex flex-col gap-1 overflow-y-auto rounded-lg border border-border/50 bg-muted/30 px-3 py-2' as const

/**
 * Shared dashed empty/error shell — CrewFeedList error recovery panel.
 * Prefer over re-inlining border-dashed empty chrome.
 */
export const CREW_SURFACE_DASHED_CLASSNAME =
  'rounded-2xl border border-dashed border-border/70' as const

/**
 * Shared inset row shell — Scheduled list rows (tighter than PANEL).
 * Prefer over re-inlining rounded-xl border bg-background/60.
 */
export const CREW_SURFACE_INSET_ROW_CLASSNAME =
  'rounded-xl border border-border/50 bg-background/60' as const

/**
 * Shared section header stack — InterestBrief / ScheduledList title+hint.
 * Prefer over re-inlining mb-3 space-y-1 thrash.
 */
export const CREW_SURFACE_SECTION_HEADER_CLASSNAME =
  'mb-3 space-y-1' as const

/**
 * Shared empty-state toolbar — Scheduled empty + Add CTA row.
 * Prefer over re-inlining flex-col/sm:flex-row thrash.
 */
export const CREW_SURFACE_EMPTY_TOOLBAR_CLASSNAME =
  'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between' as const

/**
 * Shared action wrap under inset rows — Scheduled pause/edit/delete.
 * Prefer over re-inlining mt-3 flex flex-wrap gap-2 thrash.
 */
export const CREW_SURFACE_ACTION_WRAP_CLASSNAME =
  'mt-3 flex flex-wrap gap-2' as const

/**
 * Mate / lane eyebrow chrome on live cards + empty-edition teaser mates.
 * Prefer over re-inlining text-xs tracking-wide muted thrash.
 */
export const CREW_FEED_MATE_EYEBROW_CLASSNAME =
  'text-xs font-medium tracking-wide text-muted-foreground' as const

/**
 * Empty-edition teaser cue badge (Love / Discuss / Go). Prefer over
 * re-inlining uppercase micro-badge thrash on CrewFeedEmptyEdition.
 */
export const CREW_FEED_EDITION_TEASER_CUE_BADGE_CLASSNAME =
  'mt-0.5 shrink-0 rounded-md border border-border/80 bg-background/80 px-2 py-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase' as const

/**
 * InterestBrief whisper textarea chrome. Prefer over re-inlining
 * min-h-[72px] resize-none rounded-xl thrash.
 */
export const CREW_INTEREST_BRIEF_TEXTAREA_CLASSNAME =
  'min-h-[72px] resize-none rounded-xl bg-background/80 motion-reduce:transition-none' as const

/**
 * InterestBrief schedule section divider. Prefer over re-inlining
 * mt-4 border-t border-border/50 pt-3 thrash.
 */
export const CREW_INTEREST_BRIEF_SCHEDULE_DIVIDER_CLASSNAME =
  'mt-4 border-t border-border/50 pt-3' as const

/**
 * InterestBrief heading title chrome. Prefer over re-inlining
 * text-pretty text-sm font-medium thrash.
 */
export const CREW_INTEREST_BRIEF_HEADING_CLASSNAME =
  'text-pretty text-sm font-medium tracking-tight text-foreground' as const

/**
 * InterestBrief hint / helper text chrome.
 */
export const CREW_INTEREST_BRIEF_HINT_CLASSNAME =
  'text-xs leading-relaxed text-muted-foreground' as const

/**
 * Feed list / empty-edition section heading. Prefer over re-inlining
 * text-pretty text-sm font-medium thrash (InterestBrief heading adds tracking).
 */
export const CREW_FEED_SECTION_HEADING_CLASSNAME =
  'text-pretty text-sm font-medium text-foreground' as const

/**
 * Feed list / empty-edition section toolbar (title + meta). Prefer over
 * re-inlining flex items-baseline justify-between thrash.
 */
export const CREW_FEED_SECTION_TOOLBAR_CLASSNAME =
  'flex items-baseline justify-between gap-3 px-1' as const

/**
 * Feed list / empty-edition outer stack (loading / error / filled / empty).
 * Prefer over re-inlining flex flex-col gap-3 thrash.
 */
export const CREW_FEED_LIST_STACK_CLASSNAME = 'flex flex-col gap-3' as const

/**
 * Feed card list / empty teaser list stack. Prefer over re-inlining
 * flex flex-col gap-2 thrash.
 */
export const CREW_FEED_CARDS_LIST_CLASSNAME = 'flex flex-col gap-2' as const

/**
 * Feed card hover lift + motion-reduce. Prefer over re-inlining transition /
 * hover:-translate-y / motion-reduce thrash (live card + empty teasers).
 */
export const CREW_FEED_CARD_HOVER_MOTION_CLASSNAME =
  'transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0' as const

/**
 * Mate / teaser accent wash shell (absolute left gradient). Pair with
 * opacity sibling + wash color token. Prefer over re-inlining pointer-events
 * absolute inset thrash.
 */
export const CREW_FEED_CARD_ACCENT_WASH_SHELL_CLASSNAME =
  'pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r' as const

/** Live card accent wash opacity. */
export const CREW_FEED_CARD_ACCENT_WASH_OPACITY_CLASSNAME =
  'opacity-90' as const

/** Empty teaser accent wash opacity (softer than live card). */
export const CREW_FEED_TEASER_ACCENT_WASH_OPACITY_CLASSNAME =
  'opacity-80' as const

/**
 * Form field stack (label + control). Prefer over re-inlining space-y-1.5
 * thrash (InterestBrief / MeetYourCrew).
 */
export const CREW_FEED_FIELD_STACK_CLASSNAME = 'space-y-1.5' as const

/**
 * Disclosure panel under a section toggle (InterestBrief schedule /
 * MeetYourCrew rules). Prefer over re-inlining mt-3 space-y-3 thrash.
 */
export const CREW_SECTION_DISCLOSURE_PANEL_CLASSNAME =
  'mt-3 space-y-3' as const

/**
 * Save / primary CTA row (InterestBrief / MeetYourCrew). Prefer over
 * re-inlining flex justify-end thrash.
 */
export const CREW_SECTION_SAVE_ROW_CLASSNAME = 'flex justify-end' as const

/**
 * Inline chrome row (enable switch / whisper footer). Prefer over
 * re-inlining flex items-center justify-between gap-3 thrash.
 */
export const CREW_SECTION_INLINE_ROW_CLASSNAME =
  'flex items-center justify-between gap-3' as const

/**
 * Segmented radiogroup row (monitor mode / rule tools). Prefer over
 * re-inlining flex gap-2 thrash.
 */
export const CREW_SECTION_SEGMENTED_ROW_CLASSNAME = 'flex gap-2' as const

/**
 * Chip / badge wrap row (MeetYourCrew / Member chrome / ChatComposer).
 * Prefer over re-inlining flex flex-wrap items-center gap-2 thrash.
 */
export const CREW_FEED_CHIP_WRAP_CLASSNAME =
  'flex flex-wrap items-center gap-2' as const

/**
 * Compact chip wrap (tighter gap). Prefer over re-inlining
 * flex flex-wrap gap-1.5 thrash.
 */
export const CREW_FEED_CHIP_WRAP_DENSE_CLASSNAME =
  'flex flex-wrap gap-1.5' as const

/**
 * Inline icon + label row. Prefer over re-inlining flex items-center gap-2.
 */
export const CREW_FEED_INLINE_ICON_ROW_CLASSNAME =
  'flex items-center gap-2' as const

/**
 * Avatar + identity row (Member chrome / MeetYourCrew summary). Prefer
 * over re-inlining flex items-center gap-3 thrash.
 */
export const CREW_FEED_AVATAR_ROW_CLASSNAME =
  'flex items-center gap-3' as const

/**
 * Live Feed card header (avatar + mate eyebrow stack). Prefer over
 * re-inlining flex items-start gap-3 thrash.
 */
export const CREW_FEED_CARD_HEADER_CLASSNAME =
  'flex items-start gap-3' as const

/**
 * Live Feed card inner body stack (below accent wash). Prefer over
 * re-inlining relative flex flex-col gap-2.5 thrash.
 */
export const CREW_FEED_CARD_BODY_STACK_CLASSNAME =
  'relative flex flex-col gap-2.5' as const

/**
 * Empty-edition teaser header row (mate eyebrow + cue badge). Prefer
 * over re-inlining relative flex items-start justify-between gap-3 thrash.
 */
export const CREW_FEED_TEASER_HEADER_CLASSNAME =
  'relative flex items-start justify-between gap-3' as const

/**
 * Empty-edition teaser text column (mate eyebrow + line). Prefer over
 * re-inlining min-w-0 space-y-1 thrash beside TEASER_HEADER.
 */
export const CREW_FEED_TEASER_TEXT_STACK_CLASSNAME =
  'min-w-0 space-y-1' as const

/**
 * Live Feed card mate/title copy column. Prefer over re-inlining
 * min-w-0 flex-1 space-y-1 thrash (TEASER_TEXT_STACK + flex-1).
 */
export const CREW_FEED_CARD_COPY_COLUMN_CLASSNAME =
  'min-w-0 flex-1 space-y-1' as const

/**
 * Live Feed card avatar offset beside mate eyebrow. Prefer over re-inlining
 * mt-0.5 shrink-0 thrash.
 */
export const CREW_FEED_CARD_AVATAR_OFFSET_CLASSNAME =
  'mt-0.5 shrink-0' as const

/**
 * Decorative avatar host shell (Member chrome LosiAvatar wrap). Prefer over
 * re-inlining bare shrink-0 thrash with aria-hidden.
 */
export const CREW_FEED_AVATAR_DECORATIVE_SHELL_CLASSNAME = 'shrink-0' as const

/**
 * Live Feed mate-label emphasis in eyebrow. Prefer over re-inlining
 * text-foreground/80 thrash.
 */
export const CREW_FEED_MATE_LABEL_EMPHASIS_CLASSNAME =
  'text-foreground/80' as const

/**
 * Live Feed meta separator glyph chrome (· between mate / lane / urgency).
 * Prefer over re-inlining mx-1.5 text-border thrash.
 */
export const CREW_FEED_META_SEPARATOR_CLASSNAME =
  'mx-1.5 text-border' as const

/**
 * Live Feed footer meta separator (tighter). Prefer over re-inlining
 * mx-1 text-border thrash.
 */
export const CREW_FEED_META_SEPARATOR_DENSE_CLASSNAME =
  'mx-1 text-border' as const

/**
 * Live Feed Love/Discuss/Go icon glyph. Prefer over re-inlining size-4 thrash.
 */
export const CREW_FEED_ACTION_ICON_CLASSNAME = 'size-4' as const

/**
 * Member chrome name + status column beside avatar. Prefer over re-inlining
 * min-w-0 flex-1 space-y-0.5 thrash.
 */
export const CREW_MEMBER_CHROME_COPY_COLUMN_CLASSNAME =
  'min-w-0 flex-1 space-y-0.5' as const

/**
 * Scheduled list row stack. Prefer over re-inlining space-y-3 thrash on
 * CrewScheduledList.
 */
export const CREW_SCHEDULED_LIST_STACK_CLASSNAME = 'space-y-3' as const

/**
 * Scheduled row title + cron + status nest. Prefer over re-inlining
 * space-y-1 thrash inside INSET_ROW.
 */
export const CREW_SCHEDULED_ROW_COPY_NEST_CLASSNAME = 'space-y-1' as const

/**
 * YourCrew home hero title + status nest. Prefer over re-inlining
 * space-y-2 thrash under the trio.
 */
export const CREW_YOUR_CREW_HERO_COPY_NEST_CLASSNAME =
  'space-y-2' as const

/**
 * Live Feed card shell chrome (pad + soft shadow). Focus rings stay on Love/Discuss/Go.
 * Pair with CREW_SURFACE_CARD + HOVER_MOTION. Prefer over re-inlining
 * group relative overflow / shadow / focus-within thrash.
 */
export const CREW_FEED_CARD_SHELL_CLASSNAME =
  'group relative overflow-hidden px-4 py-3.5 shadow-[0_8px_28px_rgba(15,23,42,0.04)]' as const

/**
 * Empty teaser card shell pad (lighter than live card). Pair with
 * CREW_SURFACE_PANEL + HOVER_MOTION + ENTER.
 */
export const CREW_FEED_TEASER_SHELL_CLASSNAME =
  'group relative overflow-hidden px-4 py-3' as const

/**
 * Feed list loading soft-copy status chrome. Prefer over re-inlining
 * LIST_STATUS + px-1 py-8 animate-in thrash.
 */
export const CREW_FEED_LIST_LOADING_STATUS_CLASSNAME =
  'px-1 py-8 animate-in fade-in duration-500 motion-reduce:animate-none' as const

/**
 * Feed list error dashed panel body. Prefer over re-inlining flex flex-col
 * items-center gap-3 px-4 py-8 thrash beside CREW_SURFACE_DASHED.
 */
export const CREW_FEED_LIST_ERROR_PANEL_CLASSNAME =
  'flex flex-col items-center gap-3 px-4 py-8' as const

/**
 * Feed list error / empty heading inset. Prefer over re-inlining "px-1".
 */
export const CREW_FEED_LIST_HEADING_INSET_CLASSNAME = 'px-1' as const

/**
 * Feed card enter motion (list stagger). Prefer over re-inlining
 * animate-in fade-in thrash — always pair with motion-reduce:animate-none.
 */
export const CREW_FEED_CARD_ENTER_CLASSNAME =
  'animate-in fade-in slide-in-from-bottom-2 fill-mode-both motion-reduce:animate-none' as const

/** List card enter stagger — base delay before first card. */
export const CREW_FEED_CARD_ENTER_BASE_DELAY_MS = 80 as const

/** List card enter stagger step between cards. */
export const CREW_FEED_CARD_ENTER_STEP_MS = 70 as const

/** Empty teaser enter stagger step (slightly slower than live list). */
export const CREW_FEED_TEASER_ENTER_STEP_MS = 90 as const

/**
 * Card / teaser enter animationDelay ms. Prefer over re-inlining
 * `${80 + index * 70}ms` thrash.
 */
export function crewFeedCardEnterDelayMs(
  index: number,
  stepMs: number = CREW_FEED_CARD_ENTER_STEP_MS,
  baseMs: number = CREW_FEED_CARD_ENTER_BASE_DELAY_MS
): number {
  const i = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0
  const step = Number.isFinite(stepMs) ? Math.max(0, stepMs) : CREW_FEED_CARD_ENTER_STEP_MS
  const base = Number.isFinite(baseMs) ? Math.max(0, baseMs) : CREW_FEED_CARD_ENTER_BASE_DELAY_MS
  return base + i * step
}

/**
 * Destination tabpanel enter (opacity + translate). Pair with EXIT on swap.
 * Always respect motion-reduce.
 */
export const CREW_DESTINATION_PANEL_ENTER_CLASSNAME =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-reduce:animate-none' as const

/**
 * Destination tabpanel exit before remount. Duration must match
 * CREW_DESTINATION_PANEL_EXIT_MS.
 */
export const CREW_DESTINATION_PANEL_EXIT_CLASSNAME =
  'motion-safe:animate-out motion-safe:fade-out motion-safe:slide-out-to-bottom-1 motion-safe:duration-150 motion-reduce:animate-none' as const

/** Exit settle ms before swapping destination panel content. */
export const CREW_DESTINATION_PANEL_EXIT_MS = 150 as const

/**
 * Destination tab base chrome — springy press + color ease (no purple glow).
 */
export const CREW_DESTINATION_TAB_CLASSNAME =
  'rounded-full px-4 text-sm touch-manipulation motion-safe:transition-[transform,box-shadow,background-color,color] motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.34,1.45,0.64,1)] motion-safe:active:scale-[0.97] motion-reduce:transition-none' as const

/** Selected destination tab — micro scale + soft ring (not glow). */
export const CREW_DESTINATION_TAB_SELECTED_CLASSNAME =
  'font-semibold text-foreground ring-1 ring-border/80 shadow-sm motion-safe:scale-[1.03]' as const

/** Idle destination tab. */
export const CREW_DESTINATION_TAB_IDLE_CLASSNAME =
  'font-medium text-foreground/85 motion-safe:scale-100' as const

/**
 * CrewStatusLine primary/secondary text remount enter.
 */
export const CREW_STATUS_LINE_TEXT_ENTER_CLASSNAME =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-reduce:animate-none' as const

/** CrewStatusLine press feedback on the hit target. */
export const CREW_STATUS_LINE_PRESS_CLASSNAME =
  'motion-safe:transition-transform motion-safe:duration-150 motion-safe:active:scale-[0.99] motion-reduce:transition-none' as const

/**
 * Side chat sheet body enter when open.
 */
export const CREW_SIDE_CHAT_CONTENT_ENTER_CLASSNAME =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-1 motion-safe:duration-300 motion-reduce:animate-none' as const

/** Side chat Done — hit joy without glow. */
export const CREW_SIDE_CHAT_DONE_CLASSNAME =
  'text-xs font-medium motion-safe:transition-[transform,background-color] motion-safe:duration-150 motion-safe:ease-out motion-safe:active:scale-[0.96] motion-safe:hover:bg-muted/60 motion-reduce:transition-none' as const

/**
 * Talk empty state — soft enter + hierarchy nest.
 */
export const CREW_TALK_EMPTY_CLASSNAME =
  'flex max-w-sm flex-col items-center gap-2.5 px-1 text-center motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-400 motion-reduce:animate-none' as const

export const CREW_TALK_EMPTY_TITLE_CLASSNAME =
  'text-sm font-medium tracking-tight text-foreground/90' as const

export const CREW_TALK_EMPTY_BODY_CLASSNAME =
  'max-w-[22rem] text-xs leading-relaxed text-muted-foreground' as const

/**
 * Feed trust strips (autonomy / approvals / overnight) — stagger enter feel
 * without skeleton theater. Pair with crewFeedCardEnterDelayMs + TRUST step.
 */
export const CREW_TRUST_STRIP_ENTER_CLASSNAME =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-400 fill-mode-both motion-reduce:animate-none' as const

/** Trust strip stagger base delay. */
export const CREW_TRUST_STRIP_ENTER_BASE_MS = 40 as const

/** Trust strip stagger step between strips. */
export const CREW_TRUST_STRIP_ENTER_STEP_MS = 70 as const

/**
 * Approval stack Allow / Not now / Edit — press joy on solid cards (no glass).
 */
export const CREW_APPROVAL_ACTION_PRESS_CLASSNAME =
  'motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-out motion-safe:active:scale-[0.97] motion-reduce:transition-none' as const

/**
 * Approval card article enter — light fade+travel; no skeleton theater.
 */
export const CREW_APPROVAL_CARD_ENTER_CLASSNAME =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-reduce:animate-none' as const

/**
 * Overnight / trust row press (solid list rows).
 */
export const CREW_TRUST_ROW_PRESS_CLASSNAME =
  'motion-safe:transition-[transform,background-color] motion-safe:duration-150 motion-safe:ease-out motion-safe:active:scale-[0.99] motion-reduce:transition-none' as const

/**
 * Scheduled list / InterestBrief muted status line.
 */
export const CREW_FEED_STATUS_MUTED_CLASSNAME =
  'text-xs text-muted-foreground' as const

/**
 * Status muted + truncate (Planner sublines / member chips). Prefer over
 * re-inlining STATUS_MUTED + truncate thrash.
 */
export const CREW_FEED_STATUS_MUTED_TRUNCATE_CLASSNAME =
  'truncate text-xs text-muted-foreground' as const

/**
 * Status muted block + truncate (Planner nested labels). Prefer over
 * re-inlining STATUS_MUTED + block truncate thrash.
 */
export const CREW_FEED_STATUS_MUTED_BLOCK_TRUNCATE_CLASSNAME =
  'block truncate text-xs text-muted-foreground' as const

/**
 * Status muted meta wrap (Planner event chips). Prefer over re-inlining
 * mt-1 flex-wrap gap-x-2 thrash.
 */
export const CREW_FEED_STATUS_MUTED_META_WRAP_CLASSNAME =
  'mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground' as const

/**
 * Scheduled list cron / expression mono chrome.
 */
export const CREW_FEED_SCHEDULE_CRON_CLASSNAME =
  'font-mono text-[11px] text-muted-foreground' as const

/**
 * Scheduled list Quiet / status detail line.
 */
export const CREW_FEED_SCHEDULE_STATUS_DETAIL_CLASSNAME =
  'text-[11px] leading-relaxed text-muted-foreground' as const

/** Scheduled / inset row title. */
export const CREW_FEED_ROW_TITLE_CLASSNAME =
  'text-sm font-medium text-foreground' as const

/**
 * InterestBrief character-count chrome (tabular-nums).
 */
export const CREW_INTEREST_BRIEF_COUNT_CLASSNAME =
  'text-[11px] tabular-nums text-muted-foreground' as const

/**
 * Feed card title — mate-authored headline.
 */
export const CREW_FEED_CARD_TITLE_CLASSNAME =
  'text-pretty break-words text-sm font-semibold leading-snug text-foreground' as const

/**
 * Feed card summary body.
 */
export const CREW_FEED_CARD_SUMMARY_CLASSNAME =
  'break-words text-sm leading-relaxed text-muted-foreground line-clamp-6' as const

/**
 * Feed card why confession (italic muted).
 */
export const CREW_FEED_CARD_WHY_CLASSNAME =
  'break-words text-xs italic leading-relaxed text-muted-foreground/90 line-clamp-4' as const

/**
 * Feed card Ask handoff note body.
 */
export const CREW_FEED_CARD_ASK_NOTICE_CLASSNAME =
  'break-words text-xs leading-relaxed text-muted-foreground' as const

/**
 * Feed card action footer strip (Love / Discuss / Go). Prefer over
 * re-inlining flex flex-wrap gap-2 pt-1 thrash.
 */
export const CREW_FEED_CARD_FOOTER_CLASSNAME =
  'flex flex-wrap items-center gap-2 pt-1' as const

/**
 * Feed card Go control host (pushes Go to the trailing edge). Prefer over
 * re-inlining ml-auto thrash.
 */
export const CREW_FEED_CARD_GO_WRAP_CLASSNAME = 'ml-auto' as const

/**
 * Feed card Go button motion (hover nudge + reduced-motion silence). Prefer
 * over re-inlining transition-transform thrash on Go.
 */
export const CREW_FEED_CARD_GO_BUTTON_CLASSNAME =
  'px-3 transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none' as const

/** Ask handoff notice title emphasis. */
export const CREW_FEED_CARD_ASK_NOTICE_TITLE_CLASSNAME =
  'font-medium text-foreground/80' as const

/**
 * Member chrome / serif section heading (Your crew scaffolds).
 */
export const CREW_FEED_SERIF_SECTION_HEADING_CLASSNAME =
  'font-serif text-lg tracking-tight text-foreground' as const

/**
 * Dense helper / custom-rules honesty (11px + muted/90).
 */
export const CREW_FEED_DENSE_HONESTY_NOTE_CLASSNAME =
  'text-[11px] leading-relaxed text-muted-foreground/90' as const

/**
 * Empty edition / feed list meta count (tabular-nums xs).
 */
export const CREW_FEED_META_TABULAR_CLASSNAME =
  'text-xs tabular-nums text-muted-foreground' as const

/**
 * Empty edition soft body / silenced / schedule hints.
 */
export const CREW_FEED_SOFT_BODY_CLASSNAME =
  'px-1 text-sm leading-relaxed text-muted-foreground' as const

/**
 * Edition / list closing soft line (centered).
 */
export const CREW_FEED_END_LINE_CLASSNAME =
  'px-1 pt-1 text-center text-xs text-muted-foreground' as const

/**
 * Empty teaser body line under mate eyebrow.
 */
export const CREW_FEED_TEASER_LINE_CLASSNAME =
  'text-sm text-foreground' as const

/**
 * Feed list loading / unavailable centered status.
 */
export const CREW_FEED_LIST_STATUS_CLASSNAME =
  'text-center text-sm text-muted-foreground' as const

/**
 * MeetYourCrew field label (muted + medium).
 */
export const CREW_MEET_YOUR_CREW_FIELD_LABEL_CLASSNAME =
  'text-xs font-medium text-muted-foreground' as const

/** MeetYourCrew picker title (serif xl). */
export const CREW_MEET_YOUR_CREW_TITLE_CLASSNAME =
  'text-pretty font-serif text-xl tracking-tight text-foreground' as const

/** MeetYourCrew hint under title. */
export const CREW_MEET_YOUR_CREW_HINT_CLASSNAME =
  'text-sm text-muted-foreground' as const

/**
 * Member chrome display name.
 */
export const CREW_MEMBER_DISPLAY_NAME_CLASSNAME =
  'truncate text-sm font-semibold text-foreground' as const

/**
 * Shared text Input chrome — InterestBrief / MeetYourCrew / ChatComposer.
 * HIG 44pt (was default h-10). Prefer this over surface-named forks.
 */
export const CREW_FEED_TEXT_INPUT_CLASSNAME =
  'h-auto min-h-[44px] rounded-xl bg-background/80 text-sm' as const

/**
 * Growing text input beside an inline action (Chat composer). Prefer over
 * re-inlining min-w-0 flex-1 thrash next to CREW_FEED_TEXT_INPUT_CLASSNAME.
 */
export const CREW_FEED_INPUT_GROW_CLASSNAME = 'min-w-0 flex-1' as const

/**
 * Alias of CREW_FEED_TEXT_INPUT_CLASSNAME — keep for hosts/parity that still
 * import the InterestBrief-named token. Prefer CREW_FEED_TEXT_INPUT_CLASSNAME.
 */
export const CREW_INTEREST_BRIEF_INPUT_CLASSNAME = CREW_FEED_TEXT_INPUT_CLASSNAME

/**
 * Shared micro status badge (MeetYourCrew draft status / empty teaser cues
 * cousins). Prefer over re-inlining uppercase text-[10px] border thrash.
 */
export const CREW_FEED_MICRO_BADGE_CLASSNAME =
  'rounded-md border border-border/60 px-2 py-0.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase' as const

/** Denser micro badge (px-1.5). */
export const CREW_FEED_MICRO_BADGE_DENSE_CLASSNAME =
  'rounded-md border border-border/60 px-1.5 py-0.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase' as const

/**
 * MeetYourCrew mate chip button chrome (pair with CREW_FEED_ACTION_HIT_STYLE).
 */
export const CREW_MEET_YOUR_CREW_MATE_CHIP_CLASSNAME =
  'min-w-[44px] rounded-md px-2.5 text-xs touch-manipulation motion-safe:transition-[transform,background-color,box-shadow] motion-safe:duration-150 motion-safe:ease-out motion-safe:active:scale-[0.96] motion-reduce:transition-none' as const

/**
 * MeetYourCrew honesty note chrome (role=note body).
 */
export const CREW_MEET_YOUR_CREW_HONESTY_NOTE_CLASSNAME =
  'text-xs leading-relaxed text-muted-foreground/90' as const

/**
 * MeetYourCrew draft-fields divider.
 */
export const CREW_MEET_YOUR_CREW_FIELDS_DIVIDER_CLASSNAME =
  'border-t border-border/50 pt-3' as const

/**
 * YourCrew home hero eyebrow (uppercase tracking). Prefer over re-inlining
 * text-xs tracking-[0.18em] uppercase thrash on your-crew-home.
 */
export const CREW_YOUR_CREW_HERO_EYEBROW_CLASSNAME =
  'text-xs font-medium tracking-[0.18em] text-muted-foreground uppercase' as const

/**
 * YourCrew home hero status/blurb chrome under the title.
 */
export const CREW_YOUR_CREW_HERO_STATUS_CLASSNAME =
  'mx-auto max-w-md text-sm leading-relaxed text-muted-foreground' as const

/** YourCrew home hero H1. */
export const CREW_YOUR_CREW_HERO_TITLE_CLASSNAME =
  'text-pretty font-serif text-3xl tracking-tight text-foreground md:text-4xl' as const

/** YourCrew home content column. */
export const CREW_YOUR_CREW_HOME_COLUMN_CLASSNAME =
  'relative mx-auto flex w-full max-w-2xl flex-col gap-10 px-6 py-12 md:px-10 md:py-16' as const

/**
 * YourCrew home outer shell. Prefer over re-inlining
 * relative flex size-full min-h-0 flex-col thrash.
 */
export const CREW_YOUR_CREW_HOME_SHELL_CLASSNAME =
  'relative flex size-full min-h-0 flex-col' as const

/**
 * YourCrew home scroll host. Prefer over re-inlining
 * relative min-h-0 flex-1 overflow-auto thrash.
 */
export const CREW_YOUR_CREW_HOME_SCROLL_CLASSNAME =
  'relative min-h-0 flex-1 overflow-auto' as const

/** YourCrew home hero stack. */
export const CREW_YOUR_CREW_HOME_HERO_STACK_CLASSNAME =
  'flex flex-col items-center gap-5 text-center' as const

/**
 * YourCrew home atmosphere — top radial wash. Prefer over re-inlining
 * absolute inset radial thrash on your-crew-home.
 */
export const CREW_YOUR_CREW_HOME_WASH_RADIAL_CLASSNAME =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,hsl(var(--foreground)/0.06),transparent_55%)]' as const

/** YourCrew home atmosphere — left sky bloom. */
export const CREW_YOUR_CREW_HOME_WASH_SKY_CLASSNAME =
  'pointer-events-none absolute -left-24 top-24 size-72 rounded-full bg-sky-500/10 blur-3xl' as const

/** YourCrew home atmosphere — right amber bloom. */
export const CREW_YOUR_CREW_HOME_WASH_AMBER_CLASSNAME =
  'pointer-events-none absolute -right-16 bottom-10 size-80 rounded-full bg-amber-500/10 blur-3xl' as const

/** Chat composer / YourCrew shared max-w-2xl column. */
export const CREW_CHAT_COMPOSER_COLUMN_CLASSNAME =
  'mx-auto flex w-full max-w-2xl flex-col gap-2' as const

/**
 * Sticky Chat composer form shell (Your-crew bottom bar). Prefer over
 * re-inlining sticky bottom-0 border-t backdrop thrash.
 */
export const CREW_CHAT_COMPOSER_FORM_CLASSNAME =
  'border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky bottom-0 z-10 border-t px-4 py-3 backdrop-blur md:px-6' as const

/**
 * Shared focus-visible ring — Crew disclosure / segmented controls.
 * Prefer over re-inlining the outline-none + ring thrash.
 */
export const CREW_FOCUS_VISIBLE_RING_CLASSNAME =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2' as const

/**
 * Nested focus ring — Sources / clipped card chrome (no ring-offset;
 * offset fights card clip + Button ring-primary). Prefer for in-card links.
 */
export const CREW_FOCUS_VISIBLE_RING_NESTED_CLASSNAME =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' as const

/**
 * Switch hit wrapper — HIG 44×44 around Radix Switch (visual stays h-6).
 * InterestBrief briefing enable + planner settings Show closed time.
 */
export const CREW_SWITCH_HIT_CLASSNAME =
  'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center touch-manipulation' as const

/**
 * Switch control motion gate — pair inside CREW_SWITCH_HIT_CLASSNAME.
 * Prefer over re-inlining motion-reduce on ui/switch thrash.
 */
export const CREW_SWITCH_CONTROL_CLASSNAME =
  'motion-reduce:transition-none [&_span]:motion-reduce:transition-none' as const

/**
 * Shared disclosure toggle button className — InterestBrief schedule +
 * MeetYourCrew Custom Rules. Prefer over re-inlining the focus-ring /
 * touch-manipulation thrash. min-h-[44px] locks HIG hit (parity with
 * CREW_FEED_ACTION_HIT_STYLE on sibling CTAs).
 */
export const CREW_SECTION_DISCLOSURE_BUTTON_CLASSNAME = [
  'flex w-full min-h-[44px] items-center justify-between gap-2 rounded-md text-left text-xs font-medium text-muted-foreground hover:text-foreground',
  CREW_FOCUS_VISIBLE_RING_CLASSNAME,
  'touch-manipulation disabled:pointer-events-none disabled:opacity-50'
].join(' ')

/** Disclosure expand / collapse glyphs (aria-hidden siblings). */
export const CREW_SECTION_DISCLOSURE_EXPAND_GLYPH = '+' as const
export const CREW_SECTION_DISCLOSURE_COLLAPSE_GLYPH = '−' as const

/**
 * Shared segmented option (radio) base className — InterestBrief monitor
 * mode + MeetYourCrew Custom Rules Ask|Allow|Never. Pair with
 * CREW_SECTION_SEGMENTED_OPTION_SELECTED / _IDLE via cn().
 * min-h-[44px] locks HIG hit (was py-2 ≈32pt).
 */
export const CREW_SECTION_SEGMENTED_OPTION_BASE_CLASSNAME = [
  'flex flex-1 min-h-[44px] items-center justify-center rounded-xl border px-3 py-2 text-xs font-medium touch-manipulation',
  CREW_FOCUS_VISIBLE_RING_CLASSNAME,
  'disabled:pointer-events-none disabled:opacity-50'
].join(' ')

/** Selected segmented option surface. */
export const CREW_SECTION_SEGMENTED_OPTION_SELECTED_CLASSNAME =
  'border-primary/60 bg-primary/10 text-foreground' as const

/** Idle segmented option surface. */
export const CREW_SECTION_SEGMENTED_OPTION_IDLE_CLASSNAME =
  'border-border/60 bg-background/80 text-muted-foreground hover:text-foreground' as const

/**
 * Arrow-key delta for segmented radiogroups (InterestBrief / MeetYourCrew).
 * Callers preventDefault + cycle selection — do not fork key lists in UI.
 */
export function crewSectionSegmentedRadiogroupArrowDelta(
  key: string
): -1 | 1 | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') return 1
  if (key === 'ArrowLeft' || key === 'ArrowUp') return -1
  return null
}

/**
 * Cycle radiogroup index by arrow delta (wrap). Prefer over re-inlining
 * modulo thrash in MeetYourCrew / InterestBrief.
 */
export function crewSectionSegmentedCycleIndex(
  current: number,
  delta: -1 | 1,
  length: number
): number {
  if (!Number.isFinite(length) || length <= 0) return 0
  const base = Number.isFinite(current) ? Math.floor(current) : 0
  return ((base % length) + length + delta) % length
}

/**
 * After arrow-key selection in a segmented radiogroup, move focus to the
 * newly checked radio (APG roving tabindex). Prefer over hosts forgetting
 * to focus after setState.
 */
export function crewSectionSegmentedFocusChecked(
  group: HTMLElement | null
): void {
  if (!group) return
  queueMicrotask(() => {
    const el = group.querySelector<HTMLElement>(
      '[role="radio"][aria-checked="true"]'
    )
    el?.focus()
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  })
}
/**
 * Closed set of Love / Discuss / Go / Unlove chrome mirrored on mobile
 * feed-card-types. Prefer named CREW_FEED_ACTION_* at call sites; use this
 * object for parity iteration / drift audits only (not a second copy source).
 * Metro cannot import web/ — mobile mirrors the named exports only (no
 * CREW_FEED_ACTION_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-discuss-chrome-parity.test.ts`.
 */
export const CREW_FEED_ACTION_MIRROR = {
  CREW_FEED_ACTION_LOVE,
  CREW_FEED_ACTION_UNLOVE,
  CREW_FEED_ACTION_DISCUSS,
  CREW_FEED_ACTION_GO,
  CREW_FEED_ACTION_MIN_HEIGHT_PX
} as const

/**
 * Ask CTA handoff chrome — live card honesty line + toast / Alert when Go
 * is Ask-gated (`propose_mutate`, or `ics` without a calendar href → Ask
 * chat seed). Seeds chat via buildCrewAskPrompt; does not open a separate
 * Ask sheet. Never success / done / completed chrome on Ask-kind cards.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export const CREW_ASK_HANDOFF_TITLE = 'Ask' as const
export const CREW_ASK_HANDOFF_DESCRIPTION = 'Approve' as const
/** Empty — silence when Ask Go href is missing (anti-slop; no Discuss invent). */
export const CREW_ASK_HANDOFF_FALLBACK = '' as const

/** Ask mutate-candidate silence when no mutate/action/draft/label (anti-slop). */
export const CREW_ASK_MUTATE_FALLBACK = '' as const
/** Ask context silence when summary+title empty (anti-slop — no "this card"). */
export const CREW_ASK_CONTEXT_FALLBACK = '' as const

/**
 * Closed set of Ask handoff chrome mirrored on mobile feed-card-types.
 * Prefer named CREW_ASK_HANDOFF_* at call sites; use this object for parity
 * iteration / drift audits only (not a second copy source). Metro cannot
 * import web/ — mobile mirrors the named exports only (no
 * CREW_ASK_HANDOFF_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-ask-cta-handoff-parity.test.ts`.
 */
export const CREW_ASK_HANDOFF_MIRROR = {
  CREW_ASK_HANDOFF_TITLE,
  CREW_ASK_HANDOFF_DESCRIPTION,
  CREW_ASK_HANDOFF_FALLBACK
} as const

/**
 * Love writeback failure soft-copy — toast / Alert when thumbs_up POST fails.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export const CREW_LOVE_SAVE_ERROR = 'Could not' as const

/**
 * Love writeback success soft-copy — toast / Alert after thumbs_up lands.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export const CREW_LOVE_SAVE_SUCCESS = 'Saved' as const

/**
 * Closed set of Love-save soft-copy mirrored on mobile feed-card-types.
 * Prefer named CREW_LOVE_SAVE_* at call sites; use this object for parity
 * iteration / drift audits only (not a second copy source). Metro cannot
 * import web/ — mobile mirrors the named exports only (no
 * CREW_LOVE_SAVE_ERROR_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-love-whisper-toast-copy-parity.test.ts`.
 */
export const CREW_LOVE_SAVE_ERROR_MIRROR = {
  CREW_LOVE_SAVE_ERROR,
  CREW_LOVE_SAVE_SUCCESS
} as const

/** Re-export feed shape id from mates (peta / jovi / etoile). */
export type { CrewFeedMateId }

export type CrewFeedCtaKind =
  | 'deep_link'
  | 'open_chat'
  | 'ics'
  | 'open_url'
  | 'propose_mutate'

export type CrewFeedSource = {
  title: string
  url?: string
  href?: string
  retrieved_at?: string
}

/**
 * Resolve a Crew feed source link — prefer href, fall back to url.
 * Shared by web + mobile CrewFeedSources (keep identical on mobile).
 */
export function crewFeedSourceHref(
  source: Pick<CrewFeedSource, 'href' | 'url'>
): string | undefined {
  const href = source.href || source.url
  return typeof href === 'string' && href.trim() ? href.trim() : undefined
}

/**
 * True when a resolved source href leaves the app (http/https).
 * Shared by web CrewFeedSources (target=_blank) + mobile open-external a11y /
 * navigateCrewFeedHref — do not re-inline `/^https?:\/\//i`.
 */
export function crewFeedSourceIsExternal(href: string | undefined): boolean {
  return typeof href === 'string' && /^https?:\/\//i.test(href.trim())
}

/**
 * Affordance copy when a source opens outside the app (browser / new tab).
 * Shared by web + mobile CrewFeedSources — do not fork "Opens in browser".
 */
export const CREW_FEED_SOURCE_EXTERNAL_HINT = 'Web' as const

/**
 * Max height (px) for CrewFeedSources scroll region — web + mobile lockstep.
 * Prefer this over forking `max-h-32` / `maxHeight: 128` in components.
 */
export const CREW_FEED_SOURCES_MAX_HEIGHT_PX = 128 as const

/**
 * RN Pressable hitSlop for dense source rows — expands touch to ≥44pt
 * without growing the maxHeight chrome (HIG). Web pairs with
 * min-h-[44px] on the <a>. Vertical 14+14 + ~16lh ≈ 44.
 */
export const CREW_FEED_SOURCE_HIT_SLOP = {
  top: 14,
  bottom: 14,
  left: 8,
  right: 8
} as const

/**
 * Web source-row anchor className — HIG 44pt inline hit + focus ring.
 * Prefer over re-inlining in CrewFeedSources.
 */
export const CREW_FEED_SOURCE_LINK_CLASSNAME = [
  'inline-flex min-h-[44px] items-center touch-manipulation underline-offset-2 hover:underline',
  CREW_FOCUS_VISIBLE_RING_NESTED_CLASSNAME
].join(' ')
/**
 * Accessible name prefix for the sources list on a card — invent silence
 * (card title is enough). Shared by web + mobile — compose via
 * crewFeedSourcesListLabel. Do not re-inline "Sources for" in card chrome.
 */
export const CREW_FEED_SOURCES_LIST_PREFIX = '' as const

/**
 * Accessible name prefix for a single tappable source row.
 * Shared by web + mobile — compose via crewFeedSourceOpenLabel.
 * Do not re-inline "Open source" in CrewFeedSources.
 */
export const CREW_FEED_SOURCE_OPEN_PREFIX = 'Open' as const

/**
 * Closed set of source-row soft-copy mirrored on mobile feed-card-types.
 * Prefer named CREW_FEED_SOURCE_* / CREW_FEED_SOURCES_* at call sites; use
 * this object for parity iteration / drift audits only (not a second copy
 * source). Metro cannot import web/ — mobile mirrors the named exports only
 * (no CREW_FEED_SOURCE_CHROME_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-feed-source-href.test.ts`.
 */
export const CREW_FEED_SOURCE_CHROME_MIRROR = {
  CREW_FEED_SOURCE_EXTERNAL_HINT,
  CREW_FEED_SOURCES_MAX_HEIGHT_PX,
  CREW_FEED_SOURCE_HIT_SLOP,
  CREW_FEED_SOURCE_LINK_CLASSNAME,
  CREW_FEED_SOURCES_LIST_PREFIX,
  CREW_FEED_SOURCE_OPEN_PREFIX
} as const

/**
 * Affordance copy when a source opens outside the app (browser / new tab).
 * Shared by web + mobile CrewFeedSources — prefer CREW_FEED_SOURCE_EXTERNAL_HINT.
 */
export function crewFeedSourceExternalHint(): string {
  return CREW_FEED_SOURCE_EXTERNAL_HINT
}

/**
 * Accessible name for the sources list on a card.
 * When CREW_FEED_SOURCES_LIST_PREFIX is silenced (''), return '' — do not
 * reuse the card title as the region name (double-speak with the card
 * heading; overflow focus would re-read the same title). Hosts omit
 * aria-label / region role when empty.
 * Shared by web + mobile CrewFeedCard → CrewFeedSources label prop.
 * Compose from CREW_FEED_SOURCES_LIST_PREFIX — do not re-inline
 * `Sources for ${title}` in card chrome.
 */
export function crewFeedSourcesListLabel(title: string): string {
  const name = typeof title === 'string' ? title.trim() : ''
  if (!CREW_FEED_SOURCES_LIST_PREFIX) return ''
  if (name) return `${CREW_FEED_SOURCES_LIST_PREFIX} ${name}`
  return CREW_FEED_SOURCES_LIST_PREFIX
}

/**
 * Accessible name for a single tappable source row.
 * Shared by web + mobile CrewFeedSources — compose from
 * CREW_FEED_SOURCE_OPEN_PREFIX (+ EXTERNAL_HINT when open-external).
 * Pass external=true when crewFeedSourceIsExternal(href) so open-external
 * affordance stays in the spoken name (web ↔ mobile parity).
 * Empty title → prefix only (anti-slop; no trailing-space invent).
 */
export function crewFeedSourceOpenLabel(
  title: string,
  external = false
): string {
  const name = typeof title === 'string' ? title.trim() : ''
  const base = name
    ? `${CREW_FEED_SOURCE_OPEN_PREFIX} ${name}`
    : CREW_FEED_SOURCE_OPEN_PREFIX
  return external
    ? `${base} (${CREW_FEED_SOURCE_EXTERNAL_HINT.toLowerCase()})`
    : base
}

/**
 * Stable React / RN key for a source crumb row.
 * Shared by web + mobile CrewFeedSources — do not fork `${title}-${index}`.
 */
export function crewFeedSourceRowKey(
  source: Pick<CrewFeedSource, 'title'>,
  index: number
): string {
  return `${source.title}-${index}`
}

/**
 * True when sources should silence chrome (null / undefined / non-array / empty).
 * Shared by web + mobile CrewFeedSources + crewFeedCardHasSources.
 * Do not re-inline `.length === 0` — keep identical on mobile feed-card-types.
 */
export function crewFeedSourcesAreEmpty(
  sources: readonly CrewFeedSource[] | null | undefined
): boolean {
  return !Array.isArray(sources) || sources.length === 0
}

/**
 * Visible Love control label — Love when idle, Unlove when pressed.
 * Shared by web + mobile CrewFeedCard — compose from CREW_FEED_ACTION_*.
 * Do not re-inline Love / Unlove chrome in card JSX.
 */
export function crewFeedLoveActionLabel(loved: boolean): string {
  return loved ? CREW_FEED_ACTION_UNLOVE : CREW_FEED_ACTION_LOVE
}

/**
 * Accessible name for the Love control.
 * Shared by web + mobile CrewFeedCard — compose from CREW_FEED_ACTION_*.
 * Do not re-inline Love / Unlove chrome in card JSX.
 */
export function crewFeedLoveAriaLabel(
  card: { title: string },
  loved: boolean
): string {
  return `${crewFeedLoveActionLabel(loved)} ${card.title}`
}

/**
 * Accessible / title name for the Discuss control.
 * Seeded solo Losi chat about the card — not a Crew group thread.
 * Shared by web + mobile CrewFeedCard — compose from CREW_FEED_ACTION_DISCUSS.
 * Do not re-inline Discuss chrome in card JSX.
 */
export function crewFeedDiscussAriaLabel(card: {
  title: string
  mateLabel: string
}): string {
  return `${CREW_FEED_ACTION_DISCUSS} ${card.title} in chat`
}

/**
 * Untitled display theater — exact "Untitled" or "Untitled …" prefix.
 * Shared by normalize + proactive + digester — silence rather than show.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */

/**
 * Strip legacy "Continue: …" title theater (anti-slop). Display the thread
 * name only — chatNameFromRow already parses the same regex for why/name.
 */
export function crewFeedStripContinueTitlePrefix(title: string): string {
  const trimmed = typeof title === 'string' ? title.trim() : ''
  if (!trimmed) return ''
  const continueMatch = trimmed.match(/^Continue:\s*(.+)$/i)
  const rest = continueMatch?.[1]?.trim()
  return rest || trimmed
}

export function crewFeedIsUntitledTheater(
  value: string | null | undefined
): boolean {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^untitled(\b|$)/i.test(trimmed)
}

/**
 * Display-title scrub — strip "Continue: …" then silence Untitled theater.
 * Prefer over re-forking strip + Untitled checks at normalize / digest /
 * proactive / CRM crumb / source map sites.
 */
export function crewFeedScrubDisplayTitle(
  value: string | null | undefined
): string {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) return ''
  const stripped = crewFeedStripContinueTitlePrefix(raw)
  return stripped && !crewFeedIsUntitledTheater(stripped) ? stripped : ''
}

/**
 * Scrub stored Go labels — empty / "Do it" execute theater → CREW_FEED_ACTION_GO.
 * Shared by crewFeedGoLabel + buildPrimaryCta. Keep identical in mobile.
 */
export function crewFeedScrubGoLabel(
  label: string | null | undefined
): string {
  const raw = typeof label === 'string' ? label.trim() : ''
  if (!raw || /^do it$/i.test(raw) || /^go$/i.test(raw)) return CREW_FEED_ACTION_GO
  // Detail route absent — "Open lead" overclaims list deep-link (?leadId=).
  if (!CRM_LEAD_DETAIL_ROUTE_EXISTS && /^open lead$/i.test(raw)) {
    return CREW_FEED_ACTION_GO
  }
  return raw
}

/**
 * Visible Go control label — primaryCta.label scrubbed, or CREW_FEED_ACTION_GO.
 * `propose_mutate` Ask CTAs with empty/default "Go" coerce to
 * CREW_ASK_HANDOFF_TITLE so the control matches Ask · Approve note chrome
 * (not "Go" while the note says Ask). Calendar `ics` keeps scrubbed label —
 * Ask-ics vs calendar-ics needs href; do not over-coerce.
 * Shared by web + mobile CrewFeedCard — do not re-inline
 * `primaryCta?.label ?? CREW_FEED_ACTION_GO` in card JSX.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedGoLabel(card: {
  primaryCta?: { label?: string | null; kind?: string | null } | null
}): string {
  const scrubbed = crewFeedScrubGoLabel(card.primaryCta?.label)
  if (
    card.primaryCta?.kind === 'propose_mutate' &&
    scrubbed === CREW_FEED_ACTION_GO
  ) {
    return CREW_ASK_HANDOFF_TITLE
  }
  return scrubbed
}

/**
 * Go control icon map key — primaryCta.icon, or CREW_FEED_GO_ICON_DEFAULT.
 * Shared by web + mobile CrewFeedCard — do not re-inline
 * `primaryCta?.icon ?? CREW_FEED_GO_ICON_DEFAULT` in card JSX.
 * ICON_MAP stays platform-forked (Tabler vs Lucide); this is the string key only.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedGoIconKey(card: {
  primaryCta?: { icon?: string | null } | null
}): string {
  return card.primaryCta?.icon ?? CREW_FEED_GO_ICON_DEFAULT
}

/**
 * Accessible name for the Go control.
 * Shared by web + mobile CrewFeedCard — do not re-inline `${goLabel}: ${title}`.
 * Prefer crewFeedGoLabel(card) for the visible/goLabel arg.
 */
export function crewFeedGoAriaLabel(
  card: { title: string },
  goLabel: string
): string {
  return `${goLabel}: ${card.title}`
}

/**
 * Presentational lane chip — snake_case → spaced, CSS-capitalize-equivalent
 * (first letter of each word). Shared by web + mobile CrewFeedCard meta.
 * Do not re-inline `lane.replace('_', ' ')` or CSS `capitalize` forks.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedLaneLabel(lane: string): string {
  const spaced = String(lane ?? '')
    .replace(/_/g, ' ')
    .trim()
  if (!spaced) return ''
  return spaced.replace(/(^|\s)\S/g, m => m.toUpperCase())
}

/**
 * Middle-dot between mateLabel and lane chip on CrewFeedCard meta row.
 * Shared by web + mobile — do not re-inline `·` in card chrome.
 * Web mounts the bare glyph + CSS mx; mobile mounts the padded join via
 * CREW_FEED_CARD_META_SEPARATOR_PAD (no local `` ` ${·} ` `` fork).
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export const CREW_FEED_CARD_META_SEPARATOR = '·' as const

/**
 * Padded meta join for RN text nodes (` · `). Compose from
 * CREW_FEED_CARD_META_SEPARATOR — do not re-inline spaced dots in card JSX.
 * Web keeps CSS `mx-1.5` around the bare glyph; mobile CrewFeedCard mounts
 * this constant. Metro cannot import web/.
 */
export const CREW_FEED_CARD_META_SEPARATOR_PAD =
  ` ${CREW_FEED_CARD_META_SEPARATOR} ` as const

/**
 * Closed set of mate · lane meta separators mirrored on mobile
 * feed-card-types. Prefer named CREW_FEED_CARD_META_SEPARATOR(_PAD) at call
 * sites; use this object for parity iteration / drift audits only (not a
 * second copy source). Metro cannot import web/ — mobile mirrors the named
 * exports only (no CREW_FEED_CARD_META_SEPARATOR_MIRROR fork). Bun locks
 * value equality in `__tests__/lib/crew-feed-card-why-meta-parity.test.ts`.
 */
export const CREW_FEED_CARD_META_SEPARATOR_MIRROR = {
  CREW_FEED_CARD_META_SEPARATOR,
  CREW_FEED_CARD_META_SEPARATOR_PAD
} as const

/**
 * Presentational why body — trim for gate + display.
 * Shared by web + mobile CrewFeedCard — do not re-inline `card.why.trim()`.
 * Empty / whitespace-only → '' (silence the why row).
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedCardWhyText(why: string): string {
  return String(why ?? '').trim()
}

/**
 * Whether the card exposes a mate why line (trimmed non-empty).
 * Actionable Crew cards must pass this AND crewFeedCardHasGo (§1e-5 slice 3).
 * Prefer over re-inlining crewFeedCardWhyText(...).length in hosts/tests.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedCardHasWhy(card: Pick<CrewFeedCard, 'why'>): boolean {
  return crewFeedCardWhyText(card.why).length > 0
}

/**
 * Accessible name for the whole CrewFeedCard root.
 * Shared by web + mobile — do not re-inline `${mate} ${lane}: ${title}`.
 * Lane chip via crewFeedLaneLabel so spoken name matches visible meta.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedCardAriaLabel(card: {
  mateLabel: string
  lane: string
  title: string
  urgency?: CrewFeedUrgency | null
}): string {
  const urgencyLabel = crewFeedUrgencyLabel(card.urgency ?? undefined)
  const urgencyPart = urgencyLabel ? ` ${urgencyLabel}` : ''
  return `${card.mateLabel} ${crewFeedLaneLabel(card.lane)}${urgencyPart}: ${card.title}`
}

/**
 * primary_cta — the mandatory Go icon → act control.
 * `propose_mutate` is Ask-gated: label+icon+payload draft, href optional
 * (host confirms later — do not require navigation href).
 */
export type CrewFeedPrimaryCta = {
  icon: string
  label: string
  kind: CrewFeedCtaKind
  payload: Record<string, unknown>
}

/**
 * Optional glance / priority depth on a Crew card (Copilot-style).
 * Additive — omit when unknown. Digests may rank via
 * `rankCrewFeedDigestCards` (feed-digest); digester opt-in via
 * `CREW_FEED_DIGEST_URGENCY_RANK=1` (exact `"1"`, default OFF).
 * Keep identical in mobile feed-card-types.
 */
export type CrewFeedUrgency = 'critical' | 'important' | 'fyi'

/** Ordered urgency catalogue — critical first for digest ranking. */
export const CREW_FEED_URGENCIES = [
  'critical',
  'important',
  'fyi'
] as const satisfies readonly CrewFeedUrgency[]

/**
 * Sort rank — lower prints first. Missing / unknown trails `fyi`.
 * Prefer `crewFeedUrgencyRank` over re-inlining these numbers.
 */
export const CREW_FEED_URGENCY_RANK = {
  critical: 0,
  important: 1,
  fyi: 2
} as const satisfies Record<CrewFeedUrgency, number>

/** Tail rank when urgency is absent or unrecognized. */
export const CREW_FEED_URGENCY_RANK_MISSING = 3

/** True when value is a catalogue urgency (CREW_FEED_URGENCIES). */
export function isCrewFeedUrgency(value: string): value is CrewFeedUrgency {
  return (CREW_FEED_URGENCIES as readonly string[]).includes(value)
}

/**
 * Soft-normalize unknown urgency tokens → catalogue value.
 * Case-insensitive exact match only — no invented priority aliases.
 * Returns undefined when missing / unrecognized (omit on the card).
 */
export function normalizeCrewFeedUrgency(
  value: unknown
): CrewFeedUrgency | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim().toLowerCase()
  return isCrewFeedUrgency(trimmed) ? trimmed : undefined
}

/**
 * Digest / glance sort key — critical (0) → important (1) → fyi (2) →
 * missing (3). Pure; safe for stable sorts.
 */
export function crewFeedUrgencyRank(
  urgency: CrewFeedUrgency | null | undefined
): number {
  if (!urgency) return CREW_FEED_URGENCY_RANK_MISSING
  return CREW_FEED_URGENCY_RANK[urgency] ?? CREW_FEED_URGENCY_RANK_MISSING
}

/**
 * Resolve optional urgency from a pulse row — top-level `urgency` column
 * first, then `source_data.urgency`. Never invents a default.
 */
export function resolveCrewFeedUrgencyFromRow(
  row: Pick<PulseCardLike, 'urgency' | 'source_data'>
): CrewFeedUrgency | undefined {
  const fromRow = normalizeCrewFeedUrgency(row.urgency)
  if (fromRow) return fromRow
  const source = getJsonRecord(row.source_data)
  return normalizeCrewFeedUrgency(getRecordString(source, 'urgency'))
}

/** Visible urgency chip labels — omit when unknown (no invented priority). */
export const CREW_FEED_URGENCY_LABELS = {
  critical: 'Critical',
  important: 'Important',
  fyi: 'FYI'
} as const satisfies Record<CrewFeedUrgency, string>

/**
 * Closed urgency label catalogue for drift audits. Prefer named
 * CREW_FEED_URGENCY_LABELS at call sites. Mobile mirrors named keys only
 * (no CREW_FEED_URGENCY_LABEL_MIRROR fork).
 */
export const CREW_FEED_URGENCY_LABEL_MIRROR = CREW_FEED_URGENCY_LABELS

/**
 * Visible urgency chip text. Empty when missing / unrecognized — do not
 * invent a default priority. Keep lockstep with mobile feed-card-types.
 */
export function crewFeedUrgencyLabel(
  urgency: CrewFeedUrgency | null | undefined
): string {
  if (!urgency || !isCrewFeedUrgency(urgency)) return ''
  return CREW_FEED_URGENCY_LABELS[urgency]
}

/** True when the card should mount urgency chrome. Prefer over re-inlining. */
export function crewFeedCardHasUrgency(card: {
  urgency?: CrewFeedUrgency | null
}): boolean {
  return Boolean(crewFeedUrgencyLabel(card.urgency))
}

/**
 * Accessible name for the urgency chip. Compose from crewFeedUrgencyLabel —
 * do not re-inline `Urgency ${…}` in card JSX.
 */
export function crewFeedUrgencyAriaLabel(
  urgency: CrewFeedUrgency | null | undefined
): string {
  const label = crewFeedUrgencyLabel(urgency)
  return label ? `Urgency ${label}` : ''
}

/**
 * Glance-weight for urgency chrome (Copilot Critical/Important/FYI depth).
 * Null when urgency absent — never invents a default. Cards map emphasis →
 * typography only (no invented color system / Muse glow).
 */
export type CrewFeedUrgencyChromeEmphasis = 'strong' | 'medium' | 'soft'

export function crewFeedUrgencyChromeEmphasis(
  urgency: CrewFeedUrgency | null | undefined
): CrewFeedUrgencyChromeEmphasis | null {
  if (!urgency || !isCrewFeedUrgency(urgency)) return null
  if (urgency === 'critical') return 'strong'
  if (urgency === 'important') return 'medium'
  return 'soft'
}

/**
 * Typography lockstep for urgency glance-weight (web CSS + mobile StyleSheet).
 * Prefer these over forking font-bold / 700 in card chrome.
 */
export const CREW_FEED_URGENCY_EMPHASIS_FONT_WEIGHT = {
  strong: '700',
  medium: '600',
  soft: '400'
} as const

export const CREW_FEED_URGENCY_EMPHASIS_OPACITY = {
  strong: 1,
  medium: 0.85,
  soft: 0.7
} as const

/**
 * Urgency label base color (emphasis via inline fontWeight/opacity). Prefer
 * over re-inlining text-foreground thrash on CrewFeedCard.
 */
export const CREW_FEED_URGENCY_LABEL_CLASSNAME = 'text-foreground' as const

/**
 * Closed urgency glance-weight catalogue (fontWeight + opacity) for drift
 * audits. Prefer named EMPHASIS_* at call sites. Mobile mirrors named
 * objects only (no CREW_FEED_URGENCY_EMPHASIS_MIRROR fork).
 */
export const CREW_FEED_URGENCY_EMPHASIS_MIRROR = {
  CREW_FEED_URGENCY_EMPHASIS_FONT_WEIGHT,
  CREW_FEED_URGENCY_EMPHASIS_OPACITY
} as const

export type CrewFeedCard = {
  id: string
  lane: CrewFeedLane
  mateId: CrewFeedMateId
  mateLabel: string
  title: string
  summary: string
  why: string
  sources: CrewFeedSource[]
  /** View-model form of pulse primary_cta / adapted action_data. */
  primaryCta: CrewFeedPrimaryCta | null
  secondary: Array<'discuss' | 'save' | 'sources' | 'love'>
  expiresAt?: string | null
  rawCardType?: string
  /**
   * Optional glance priority — omit when unknown.
   * Generators may store under `source_data.urgency` or a future column.
   */
  urgency?: CrewFeedUrgency
}

/** Loose row shape from pulse_cards / API (pre-migration columns optional).
 * `lane` / `why` / `sources` / `primary_cta` / `mate_id` match
 * CREW_PULSE_CARDS_FEED_COLUMN_NAMES — optional until writers populate
 * (soft-insert may still strip pre-migrate); adapter fills gaps (§8.6).
 */
export type PulseCardLike = {
  id: string
  title?: string | null
  description?: string | null
  card_type?: string | null
  icon?: string | null
  action_data?: unknown
  source_data?: unknown
  ai_analysis?: unknown
  source_context?: unknown
  why?: string | null
  sources?: unknown
  primary_cta?: unknown
  lane?: string | null
  mate_id?: string | null
  expires_at?: string | null
  generated_at?: string | null
  /** Optional glance priority (column or mirrored from source_data). */
  urgency?: string | null
}

/**
 * card_type → lane. `memory_highlight` → interest (Star/etoile) —
 * documented by interest-mate-lane INTEREST_RESEARCH_CARD_TYPE / LANE.
 * Keep string literals so map-mate parity extractObjectLiteral stays
 * identical to mobile/lib/crew/normalize.ts (Metro cannot import web/).
 */
const LANE_BY_CARD_TYPE: Record<string, CrewFeedLane> = {
  chat_follow_up: 'follow_up',
  project_reminder: 'follow_up',
  insight: 'insight',
  memory_highlight: 'interest',
  task_suggestion: 'ops',
  calendar_context: 'calendar'
}

// --------------

function getNumberish(record: PulseJsonRecord | null, ...keys: string[]) {
  if (!record) return null
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(Math.trunc(value))
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return null
}

/** True when value is a catalogue lane (CREW_FEED_LANES). */
export function isCrewFeedLane(value: string): value is CrewFeedLane {
  return (CREW_FEED_LANES as readonly string[]).includes(value)
}

function chatNameFromRow(row: PulseCardLike): string | null {
  const source = getJsonRecord(row.source_data)
  const fromSource = getRecordString(
    source,
    'chat_name',
    'chatName',
    'project_name',
    'projectName'
  )
  if (fromSource) {
    return crewFeedScrubDisplayTitle(fromSource) || null
  }

  const title = typeof row.title === 'string' ? row.title.trim() : ''
  if (!title) return null
  return crewFeedScrubDisplayTitle(title) || null
}

// --------------

/**
 * Map pulse card_type → CrewFeed lane (§8.1–8.5).
 * Unknown types fall back to insight.
 */
export function mapCardType(
  cardType: string | null | undefined
): CrewFeedLane {
  if (!cardType) return 'insight'
  return LANE_BY_CARD_TYPE[cardType] ?? 'insight'
}

/** @deprecated Prefer mapCardType — kept for early callers. */
export const mapPulseCardTypeToLane = mapCardType

/**
 * Lane → Your-crew mate chip. Labels/ids from mates.ts
 * (`CREW_FEED_LANE_TO_SHAPE` + `CREW_FEED_SHAPE_LABELS`).
 */
export function mateForLane(lane: CrewFeedLane) {
  return mateForLaneFromCatalog(lane)
}

/**
 * deriveWhy static fallbacks — lockstep web ↔ mobile.
 * Prefer these over re-inlining literals in the switch. Adapter / system
 * soft-copy (not mate-authored research); never invent Muse/Vesper.
 * Templates with names/days stay in deriveWhy.
 */
/**
 * Bare chat_follow_up without chat name — silence (anti-slop). Named threads
 * still use crewFeedWhyOpenThreadIn; do not invent nameless "open thread".
 */
export const CREW_FEED_WHY_FALLBACK_OPEN_THREAD = '' as const
/**
 * Bare insight without why_hint — silence (anti-slop). Do not invent
 * "From your last 7 days of chats" theater; producers supply why_hint.
 */
export const CREW_FEED_WHY_FALLBACK_INSIGHT = '' as const
export const CREW_FEED_WHY_FALLBACK_MEMORY = '' as const
/**
 * task_suggestion without why/why_hint — silence (anti-slop). Do not invent
 * "Suggested from recent conversation" theater; HasWhy gate drops bare rows.
 */
export const CREW_FEED_WHY_FALLBACK_TASK = '' as const
/**
 * calendar_context without why/why_hint — silence (anti-slop). Do not invent
 * "From your calendar context" theater; producers must supply why_hint.
 */
export const CREW_FEED_WHY_FALLBACK_CALENDAR = '' as const
/**
 * Unknown card_type — silence (empty) rather than invent mate judgment
 * (anti-slop). crewFeedCardWhyText already hides blank why rows.
 */
export const CREW_FEED_WHY_FALLBACK_DEFAULT = '' as const

/** Interest research brief why — invent prefix silenced (anti-slop); brief only. */
export const CREW_FEED_WHY_INTEREST_WATCH_PREFIX = '' as const
/** Max interest-brief chars in deriveWhy when why_hint is absent. */
export const CREW_FEED_WHY_INTEREST_BRIEF_MAX = 80 as const

/** project_reminder why — days-since invent silenced (anti-slop). */
export const CREW_FEED_WHY_PROJECT_INACTIVE_PREFIX = '' as const
export const CREW_FEED_WHY_PROJECT_INACTIVE_FOR = '' as const
export const CREW_FEED_WHY_PROJECT_INACTIVE_DAYS_SUFFIX = '' as const
/**
 * project_reminder why — no days-since fallback silenced (anti-slop).
 * Do not invent "Time to check in on…" theater; producers supply why or days.
 */
export const CREW_FEED_WHY_PROJECT_CHECK_IN_PREFIX = '' as const
/** Empty project name — silence (anti-slop), no "this project" theater. */
export const CREW_FEED_WHY_PROJECT_NAME_FALLBACK = '' as const
/** chat_follow_up why when chat name is known. */
/** Open-thread invent silenced (anti-slop) — producers supply why_hint. */
export const CREW_FEED_WHY_OPEN_THREAD_IN_PREFIX = '' as const

export const CREW_FEED_WHY_FALLBACK_MIRROR = {
  CREW_FEED_WHY_FALLBACK_OPEN_THREAD,
  CREW_FEED_WHY_FALLBACK_INSIGHT,
  CREW_FEED_WHY_FALLBACK_MEMORY,
  CREW_FEED_WHY_FALLBACK_TASK,
  CREW_FEED_WHY_FALLBACK_CALENDAR,
  CREW_FEED_WHY_FALLBACK_DEFAULT,
  CREW_FEED_WHY_INTEREST_WATCH_PREFIX,
  CREW_FEED_WHY_PROJECT_INACTIVE_PREFIX,
  CREW_FEED_WHY_PROJECT_CHECK_IN_PREFIX,
  CREW_FEED_WHY_OPEN_THREAD_IN_PREFIX
} as const

/**
 * Interest-brief why when why_hint is absent — lockstep with
 * buildInterestResearchWhy clipped-brief branch. Prefix invent silenced
 * (anti-slop); return clipped brief only.
 */
export function crewFeedWhyInterestWatchBrief(brief: string): string {
  return brief.trim().slice(0, CREW_FEED_WHY_INTEREST_BRIEF_MAX)
}

/**
 * project_reminder why soft-copy — days-since or check-in.
 * `getNumberish` returns digit strings — accept string | number.
 */
export function crewFeedWhyProjectReminder(args: {
  projectName: string
  daysSince?: number | string | null
}): string {
  // Days-since invent silenced (anti-slop). Producers must supply why /
  // why_hint; facts may live in description — do not mint "No activity on…".
  void args
  return ''
}

/**
 * chat_follow_up why when a chat name is known.
 */
export function crewFeedWhyOpenThreadIn(chatName: string): string {
  // Open-thread invent silenced (anti-slop). Producers must supply why /
  // why_hint — do not mint "You left an open thread in…" theater.
  void chatName
  return ''
}

/** Discuss seed — Tell me more invent silenced (anti-slop); helpers return seed only. */
export const CREW_FEED_DISCUSS_SEED_TELL_MORE_PREFIX = '' as const
/** Discuss seed — Remember invent silenced (anti-slop); helpers return seed only. */
export const CREW_FEED_DISCUSS_SEED_REMEMBER_PREFIX = '' as const
export const CREW_FEED_DISCUSS_SEED_REMEMBER_SUFFIX = '' as const
/** Soft fallbacks when title/description empty — silence (anti-slop), no theater. */
export const CREW_FEED_DISCUSS_SEED_FALLBACK_INSIGHT = '' as const
export const CREW_FEED_DISCUSS_SEED_FALLBACK_MEMORY = '' as const
export const CREW_FEED_DISCUSS_SEED_FALLBACK_FIND = '' as const

export function crewFeedDiscussTellMorePrompt(seed: string): string {
  const clipped = typeof seed === 'string' ? seed.trim() : ''
  return clipped
}

export function crewFeedDiscussRememberPrompt(snippet: string): string {
  const clipped = typeof snippet === 'string' ? snippet.trim() : ''
  return clipped
}

/**
 * Prefer stored why; else derive from card_type + source_data (§8).
 * Empty / whitespace `why` falls through. `ai_analysis` / `source_context`
 * never feed why (they may still drive Go via resolvePulseGoHref).
 *
 * Interest research (`memory_highlight` from `buildInterestResearchPulseCard`):
 * prefer `source_data.why_hint`, then `interest_brief`, else a neutral Crew
 * fallback — never invent Muse/Vesper copy.
 * Keep this switch identical in `mobile/lib/crew/normalize.ts`.
 */
export function deriveWhy(row: PulseCardLike): string {
  if (typeof row.why === 'string' && row.why.trim()) {
    return row.why.trim()
  }

  const source = getJsonRecord(row.source_data)
  const cardType = row.card_type ?? ''
  const chatName = chatNameFromRow(row)

  switch (cardType) {
    case 'chat_follow_up': {
      // Named-thread invent silence — only producer why / why_hint (anti-slop).
      const followWhy = getRecordString(source, 'why_hint', 'whyHint')
      return followWhy || CREW_FEED_WHY_FALLBACK_OPEN_THREAD
    }
    case 'project_reminder': {
      // Prefer producer why_hint; days-since invent silenced in helper (anti-slop).
      const projectWhy = getRecordString(source, 'why_hint', 'whyHint')
      if (projectWhy) return projectWhy
      const days = getNumberish(source, 'days_since', 'daysSinceLastActivity')
      const name =
        getRecordString(source, 'project_name', 'projectName') ||
        chatName ||
        CREW_FEED_WHY_PROJECT_NAME_FALLBACK
      return crewFeedWhyProjectReminder({
        projectName: name,
        daysSince: days
      })
    }
    case 'insight': {
      const insightWhy = getRecordString(source, 'why_hint', 'whyHint')
      if (insightWhy) return insightWhy
      return CREW_FEED_WHY_FALLBACK_INSIGHT
    }
    case 'memory_highlight': {
      // Prefer producer why_hint only — interest_brief watchlist ≠ mate why
      // (anti-slop). Empty FALLBACK_MEMORY → HasWhy drops the card.
      const whyHint = getRecordString(source, 'why_hint', 'whyHint')
      if (whyHint) return whyHint
      return CREW_FEED_WHY_FALLBACK_MEMORY
    }
    case 'task_suggestion': {
      const taskWhy = getRecordString(source, 'why_hint', 'whyHint')
      if (taskWhy) return taskWhy
      return CREW_FEED_WHY_FALLBACK_TASK
    }
    case 'calendar_context': {
      const calendarWhy = getRecordString(source, 'why_hint', 'whyHint')
      if (calendarWhy) return calendarWhy
      return CREW_FEED_WHY_FALLBACK_CALENDAR
    }
    default:
      return CREW_FEED_WHY_FALLBACK_DEFAULT
  }
}

/** @deprecated Prefer deriveWhy. */
export const deriveCrewFeedWhy = deriveWhy

/**
 * Writer-side soft-populate payload for CREW_PULSE_CARDS_FEED_COLUMN_NAMES.
 * Same adapter nucleus as normalizePulseRow (lane/why/sources/primary_cta/mate).
 * Always returns a payload — writers do not gate on dead-Go / silence (reader does).
 * Bun: `__tests__/lib/crew-pulse-feed-column-writes-contract.test.ts`.
 */
export type CrewFeedColumnWrite = {
  lane: CrewFeedLane
  why: string
  sources: CrewFeedSource[]
  primary_cta: CrewFeedPrimaryCta | null
  mate_id: CrewFeedMateId
}

/**
 * Derive additive pulse_cards Feed columns from a card_type + action/source row.
 * Prefer already-stored column values when present; else adapter (§8.6).
 * Used by PulseAgent insert soft-write — not a second normalize fork.
 */
export function buildCrewFeedColumnWrites(
  row: Omit<PulseCardLike, 'id'> & { id?: string }
): CrewFeedColumnWrite {
  const synthetic: PulseCardLike = {
    id: typeof row.id === 'string' && row.id.trim() ? row.id : 'writer-pending',
    title: row.title,
    description: row.description,
    card_type: row.card_type,
    icon: row.icon,
    action_data: row.action_data,
    source_data: row.source_data,
    ai_analysis: row.ai_analysis,
    source_context: row.source_context,
    why: row.why,
    sources: row.sources,
    primary_cta: row.primary_cta,
    lane: row.lane,
    mate_id: row.mate_id,
    expires_at: row.expires_at,
    generated_at: row.generated_at,
    urgency: row.urgency
  }

  const lane =
    synthetic.lane && isCrewFeedLane(synthetic.lane)
      ? synthetic.lane
      : mapCardType(synthetic.card_type)
  const mate = resolveFeedMate(synthetic.mate_id) ?? mateForLane(lane)
  const why = deriveWhy(synthetic)
  const sources = parseSources(synthetic)
  const primaryCta = buildPrimaryCta(synthetic, lane)

  return {
    lane,
    why,
    sources,
    primary_cta: primaryCta,
    mate_id: mate.id
  }
}

function isCrmLeadGoAction(actionType: string | null) {
  return (
    actionType === 'open_lead' ||
    actionType === 'openLead' ||
    actionType === 'open_crm_lead' ||
    actionType === 'openCrmLead' ||
    actionType === 'view_lead' ||
    actionType === 'viewLead'
  )
}

function isCrmGoAction(actionType: string | null) {
  return (
    isCrmLeadGoAction(actionType) ||
    actionType === 'open_contact' ||
    actionType === 'openContact' ||
    actionType === 'open_crm' ||
    actionType === 'openCrm' ||
    actionType === 'open_crm_contact' ||
    actionType === 'openCrmContact' ||
    actionType === 'view_contact' ||
    actionType === 'viewContact' ||
    actionType === 'open_company' ||
    actionType === 'openCompany' ||
    actionType === 'open_crm_company' ||
    actionType === 'openCrmCompany' ||
    actionType === 'view_company' ||
    actionType === 'viewCompany'
  )
}

function isPlannerGoAction(actionType: string | null) {
  return (
    actionType === 'open_planner' ||
    actionType === 'openPlanner' ||
    actionType === 'open_calendar' ||
    actionType === 'openCalendar' ||
    actionType === 'planner_day' ||
    actionType === 'plannerDay' ||
    actionType === 'calendar_context' ||
    actionType === 'calendarContext'
  )
}

/**
 * Lane-default Go icon key (ops / follow_up / calendar / else default).
 * Shared by ctaIconForLane + SpacePulse adapters — prefer over re-inlining
 * lane === 'ops' ? CREW_FEED_GO_ICON_OPS forks. Action-specific icons
 * (search / CRM / planner action) stay in ctaIconForLane.
 * Keep identical in mobile/lib/crew/feed-card-types.ts.
 */
export function crewFeedGoIconForLane(lane: CrewFeedLane): string {
  if (lane === 'calendar') return CREW_FEED_GO_ICON_PLANNER
  if (lane === 'ops') return CREW_FEED_GO_ICON_OPS
  if (lane === 'follow_up') return CREW_FEED_GO_ICON_FOLLOW_UP
  return CREW_FEED_GO_ICON_DEFAULT
}

function ctaIconForLane(lane: CrewFeedLane, actionType: string | null) {
  if (actionType === 'search_related' || actionType === 'search') {
    return CREW_FEED_GO_ICON_SEARCH
  }
  if (isCrmGoAction(actionType)) return CREW_FEED_GO_ICON_USER
  if (isPlannerGoAction(actionType)) return CREW_FEED_GO_ICON_PLANNER
  return crewFeedGoIconForLane(lane)
}

/**
 * Go button label — prefer action-specific copy (Continue / Open contact /
 * Open company / Open lead / Open planner / …) over bare CREW_FEED_ACTION_GO
 * when the action type is known. Keep lockstep with mobile/lib/crew/normalize.ts.
 */
function ctaLabelForAction(actionType: string | null, lane: CrewFeedLane) {
  // Continue only for follow-up thread resume — not every open_chat (anti-slop).
  if (actionType === 'open_chat') {
    return lane === 'follow_up'
      ? CREW_FEED_GO_LABEL_CONTINUE
      : CREW_FEED_ACTION_GO
  }
  if (actionType === 'start_chat') return CREW_FEED_GO_LABEL_START
  if (actionType === 'search_related' || actionType === 'search') {
    return CREW_FEED_GO_LABEL_SEARCH
  }
  if (isCrmLeadGoAction(actionType)) {
    // Detail `/nexus/crm/leads/:id` absent — list deep-link is not "Open lead".
    return CRM_LEAD_DETAIL_ROUTE_EXISTS
      ? CREW_FEED_GO_LABEL_OPEN_LEAD
      : CREW_FEED_ACTION_GO
  }
  if (
    actionType === 'open_company' ||
    actionType === 'openCompany' ||
    actionType === 'open_crm_company' ||
    actionType === 'view_company' ||
    actionType === 'viewCompany'
  ) {
    return CREW_FEED_GO_LABEL_OPEN_COMPANY
  }
  if (isCrmGoAction(actionType)) return CREW_FEED_GO_LABEL_OPEN_CONTACT
  if (isPlannerGoAction(actionType)) {
    return CREW_FEED_GO_LABEL_OPEN_PLANNER
  }
  // Ops lane deep-link / chat seed — honest Go (not "Do it" execute theater).
  if (lane === 'ops') return CREW_FEED_ACTION_GO
  return CREW_FEED_ACTION_GO
}

/**
 * Dead insight cards — no Discuss-as-Go invent (anti-slop). Discuss stays
 * on secondary via HasDiscuss / crewFeedDiscussHref when seed is live.
 */
function repairInsightCta(_row: PulseCardLike): CrewFeedPrimaryCta | null {
  return null
}

/**
 * Dead memory cards — no Discuss-as-Go invent (anti-slop). Discuss stays
 * on secondary via HasDiscuss / crewFeedDiscussHref when seed is live.
 */
function repairMemoryCta(_row: PulseCardLike): CrewFeedPrimaryCta | null {
  return null
}

/**
 * Prefer stored primary_cta; else adapt action_data via resolvePulseGoHref
 * (pulse-card-target nucleus). Insight / memory_highlight no longer invent
 * Discuss-as-Go when otherwise dead (secondary Discuss only).
 */
function buildPrimaryCta(
  row: PulseCardLike,
  lane: CrewFeedLane
): CrewFeedPrimaryCta | null {
  const stored = getJsonRecord(row.primary_cta)
  if (stored) {
    const kind = getRecordString(stored, 'kind') as CrewFeedCtaKind | null
    const rawLabel = getRecordString(stored, 'label')
    const icon = getRecordString(stored, 'icon')
    const payload = getJsonRecord(stored.payload) ?? {}
    // Scrub empty / "Do it" theater before accepting stored CTA.
    const label = rawLabel ? crewFeedScrubGoLabel(rawLabel) : null
    if (kind && label && icon) {
      const rawHref = getRecordString(payload, 'href', 'path', 'url')
      if (rawHref) {
        const href = rewriteAbsentCrmLeadDetailHref(rawHref)
        // Invented /leads/:id with no extractable id — refuse stored Go.
        if (!href) return null
        // Dead bare /chat — refuse stored CTA (same live gate as getPulseTarget).
        if (href.startsWith('/chat') && !isLivePulseChatHref(href)) {
          return null
        }
        // Bare planner without ?day= — refuse (match getPulseTarget fail-closed).
        {
          const pathOnly = href.split(/[?#]/)[0] || ''
          if (
            pathOnly === '/spaces/planner' &&
            !/[?&]day=/.test(href)
          ) {
            return null
          }
          // Bare `/spaces/:id` shell without item — refuse (anti-slop).
          if (
            /^\/spaces\/[^/]+\/?$/i.test(pathOnly) &&
            pathOnly !== '/spaces/planner' &&
            !/[?&]itemType=/.test(href) &&
            !/[?&]item_type=/.test(href)
          ) {
            return null
          }
        }
        if (href !== rawHref.trim()) {
          return {
            kind,
            label,
            icon,
            payload: { ...payload, href }
          }
        }
        return { kind, label, icon, payload }
      }
      if (isCrewAskGoKind(kind)) {
        // Ask without mutate seed — refuse (anti-slop; no dead Ask Go).
        const draft = getJsonRecord(payload.draft)
        const hasMutate =
          (typeof payload.mutate === 'string' &&
            payload.mutate.trim().length > 0) ||
          (typeof draft?.mutate === 'string' &&
            String(draft.mutate).trim().length > 0)
        if (!hasMutate) return null
        return { kind, label, icon, payload }
      }
    }
  }

  const actionData = getJsonRecord(row.action_data)
  const sourceData = getJsonRecord(row.source_data)
  const aiAnalysis = getJsonRecord(row.ai_analysis)
  const href = resolvePulseGoHref(actionData, sourceData, aiAnalysis)
  const actionType = getRecordString(actionData, 'type', 'action')

  if (!href) {
    if (row.card_type === 'insight') {
      return repairInsightCta(row)
    }
    if (row.card_type === 'memory_highlight') {
      return repairMemoryCta(row)
    }
    return null
  }

  // Prefer href stamp so open_crm + company_id → Open company (not Open contact);
  // lead list deep-link (?leadId=) → Open lead (not Open contact / bare Go).
  // Bare CRM tab dumps are fail-closed in getPulseTarget (null href) — if a
  // tab-only href ever reaches here, do not claim Open contact/company/lead.
  const hrefDerivedActionType =
    href.includes('/nexus/crm/contacts/')
      ? 'open_contact'
      : href.includes('/nexus/crm/companies/')
        ? 'open_company'
        : /[?&]leadId=/.test(href)
          ? 'open_lead'
          : href.startsWith('/spaces/planner')
            ? 'open_planner'
            : null
  const effectiveActionType =
    hrefDerivedActionType ||
    (isCrmGoAction(actionType) &&
    !href.includes('/nexus/crm/contacts/') &&
    !href.includes('/nexus/crm/companies/') &&
    !/[?&]leadId=/.test(href)
      ? null
      : actionType)

  // open_chat → deep_link /chat/:id; start_chat / ?query= → open_chat seed
  const kind: CrewFeedCtaKind =
    href.includes('/chat?query=') || actionType === 'start_chat'
      ? 'open_chat'
      : href.startsWith('http')
        ? 'open_url'
        : 'deep_link'

  return {
    icon: ctaIconForLane(lane, effectiveActionType),
    label: ctaLabelForAction(effectiveActionType, lane),
    kind,
    payload: { href, ...(actionData ?? {}) }
  }
}

function mapSourceEntries(entries: unknown[]): CrewFeedSource[] {
  return entries
    .map((entry) => {
      const record = getJsonRecord(entry)
      if (!record) return null
      const titleRaw = getRecordString(record, 'title', 'label')
      if (!titleRaw) return null
      // Strip "Continue: …" then silence Untitled theater (anti-slop).
      const title = crewFeedScrubDisplayTitle(titleRaw)
      if (!title) return null
      return {
        title,
        url: getRecordString(record, 'url') ?? undefined,
        href: getRecordString(record, 'href') ?? undefined,
        retrieved_at:
          getRecordString(record, 'retrieved_at', 'retrievedAt') ?? undefined
      } satisfies CrewFeedSource
    })
    .filter((entry): entry is CrewFeedSource => Boolean(entry))
}

/**
 * Prefer top-level `sources` column (post-migration); else F2 research
 * nest under `source_data.sources`; else chat / CRM contact / company
 * deep-link crumb.
 * Non-empty arrays that map to zero valid entries (title-less / junk)
 * fall through — do not block nested crumbs or secondary `sources` chrome.
 * Keep identical in mobile/lib/crew/normalize.ts (Metro cannot import web/).
 */
function parseSources(row: PulseCardLike): CrewFeedSource[] {
  if (Array.isArray(row.sources) && row.sources.length > 0) {
    const fromColumn = mapSourceEntries(row.sources)
    if (fromColumn.length > 0) return fromColumn
  }

  const source = getJsonRecord(row.source_data)
  const nested = source?.sources
  if (Array.isArray(nested) && nested.length > 0) {
    const fromNested = mapSourceEntries(nested)
    if (fromNested.length > 0) return fromNested
  }

  const chatId = getRecordString(source, 'chat_id', 'chatId')
  if (chatId) {
    const name = chatNameFromRow(row)
    // Real chat name only — no "Conversation" invent (anti-slop).
    if (name) return [{ title: name, href: `/chat/${chatId}` }]
  }

  const contactId = getRecordString(source, 'contact_id', 'contactId')
  if (contactId) {
    const nameRaw = getRecordString(
      source,
      'contact_name',
      'contactName'
    )
    // Real contact name only — no "Contact" / Untitled invent (anti-slop).
    const name = crewFeedScrubDisplayTitle(nameRaw)
    if (name) {
      const workspaceId = getRecordString(source, 'workspace_id', 'workspaceId')
      return [
        {
          title: name,
          href: buildCrmContactHref(contactId, workspaceId)
        }
      ]
    }
  }

  const companyId = getRecordString(source, 'company_id', 'companyId')
  if (companyId) {
    const nameRaw = getRecordString(
      source,
      'company_name',
      'companyName'
    )
    // Real company name only — no "Company" / Untitled invent (anti-slop).
    const name = crewFeedScrubDisplayTitle(nameRaw)
    if (name) {
      const workspaceId = getRecordString(source, 'workspace_id', 'workspaceId')
      return [
        {
          title: name,
          href: buildCrmCompanyHref(companyId, workspaceId)
        }
      ]
    }
  }

  return []
}

/**
 * Ask-gated Go kinds — actionable without a navigation href.
 * `propose_mutate` carries a draft for Ask / plan-approve; `ics` may
 * ship calendar payload only. Shared by hasGo + Go href fallback.
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function isCrewAskGoKind(
  kind: CrewFeedCtaKind | null | undefined
): boolean {
  return kind === 'propose_mutate' || kind === 'ics'
}

/**
 * Whether Go is actionable. Single contract with crewFeedGoHref — if Go
 * cannot resolve a live href (nav target, calendar URL, or Ask chat seed),
 * hasGo is false. Ask-gated kinds (`propose_mutate` / `ics`) are not
 * exempt: empty mutate+context must not mount a dead Go that only toasts.
 * Bare `/chat` (no thread, no `?query=` seed) stays dead via goHref.
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedCardHasGo(card: CrewFeedCard): boolean {
  if (!card.primaryCta) return false
  return crewFeedGoHref(card) != null
}

/**
 * Whether the live card should mount Ask-before honesty chrome
 * (`CREW_ASK_HANDOFF_TITLE` / `DESCRIPTION`). True for `propose_mutate`
 * always; for `ics` only when Go would Ask-seed (no calendar
 * href/url/path). Presentational — does not call `crewFeedGoHref`
 * (cards stay href-free). Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedCardShowsAskHandoff(
  card: Pick<CrewFeedCard, 'primaryCta'>
): boolean {
  const kind = card.primaryCta?.kind
  if (!isCrewAskGoKind(kind)) return false
  if (kind !== 'ics') return true
  const payload = card.primaryCta?.payload
  if (!payload) return true
  for (const key of ['href', 'url', 'path'] as const) {
    const value = payload[key]
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (!trimmed) continue
    // Stored Ask chat seed still shows honesty; calendar targets skip.
    if (key === 'href' && trimmed.includes('/chat?query=')) continue
    return false
  }
  return true
}

/**
 * Whether Love chrome/writeback is allowed by the card secondary allowlist.
 * Hosts still gate UI with `showLove` and writeback with `enableLove`
 * (Spaces Pulse: both false). Do not re-inline `.includes('love')` in
 * card/hook — keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedCardHasLove(
  card: Pick<CrewFeedCard, 'secondary'>
): boolean {
  return Array.isArray(card.secondary) && card.secondary.includes('love')
}

/**
 * Whether Discuss chrome/navigation is allowed by the card secondary
 * allowlist AND a live discuss href (thread or non-empty ?query= seed).
 * Refuse empty-seed dead Discuss (anti-slop) — never show Discuss that
 * navigates nowhere, with or without mateLabel.
 * Do not re-inline `.includes('discuss')` in card/hook —
 * keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedCardHasDiscuss(
  card: Pick<CrewFeedCard, 'secondary'> &
    Partial<
      Pick<CrewFeedCard, 'mateLabel' | 'summary' | 'title' | 'primaryCta'>
    >
): boolean {
  if (!Array.isArray(card.secondary) || !card.secondary.includes('discuss')) {
    return false
  }
  return Boolean(
    crewFeedDiscussHref({
      ...(card as CrewFeedCard),
      mateLabel: typeof card.mateLabel === 'string' ? card.mateLabel : '',
      summary: typeof card.summary === 'string' ? card.summary : '',
      title: typeof card.title === 'string' ? card.title : '',
      secondary: card.secondary
    }).trim()
  )
}

/**
 * Whether Sources chrome is allowed: secondary allowlist + non-empty sources.
 * Uses crewFeedSourcesAreEmpty for null/empty silence — do not re-inline
 * `.includes('sources') && sources.length` in card JSX.
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedCardHasSources(
  card: Pick<CrewFeedCard, 'secondary' | 'sources'>
): boolean {
  return (
    Array.isArray(card.secondary) &&
    card.secondary.includes('sources') &&
    !crewFeedSourcesAreEmpty(card.sources)
  )
}

/**
 * True when a pulse row claims web-research citations (F2 interest research).
 * Detects `source_data.sources` nest (incl. empty), `research_queries`,
 * non-empty top-level `sources`, interest_brief, or a why/why_hint that
 * already says `cited:`. Plain memory highlights (memory_id only) do not
 * claim — keep them. Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedRowClaimsResearchSources(
  row: Pick<PulseCardLike, 'sources' | 'source_data' | 'why'>
): boolean {
  if (Array.isArray(row.sources) && row.sources.length > 0) return true

  const source = getJsonRecord(row.source_data)
  if (source) {
    // F2 writer always nests sources (even []); key+array = research claim.
    if ('sources' in source && Array.isArray(source.sources)) return true
    if (
      Array.isArray(source.research_queries) ||
      Array.isArray(source.researchQueries)
    ) {
      return true
    }
    const interestBrief = getRecordString(
      source,
      'interest_brief',
      'interestBrief'
    )
    if (interestBrief) return true
    const whyHint = getRecordString(source, 'why_hint', 'whyHint') ?? ''
    if (/cited:/i.test(whyHint)) return true
  }

  if (typeof row.why === 'string' && /cited:/i.test(row.why)) return true
  return false
}

/**
 * §1e-5 slice 2 — sources-or-silence adapter gate for research/interest.
 * Non-interest lanes always pass (do not break ops/calendar/follow_up/insight).
 * Interest cards that never claim research sources (plain memory highlights)
 * pass. Claimed research without HasSources fails closed (drop/silence).
 * Prefer HasSources as the nucleus — do not re-inline sources.length.
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedPassesResearchSourcesOrSilenceGate(
  card: Pick<CrewFeedCard, 'lane' | 'secondary' | 'sources'>,
  claimsResearchSources: boolean
): boolean {
  // INTEREST_RESEARCH_LANE === 'interest' — keep string so Metro/web stay free
  // of a circular interest-mate-lane import from this adapter.
  if (card.lane !== 'interest' || !claimsResearchSources) return true
  // Normalize only appends secondary `sources` when non-empty; force the claim
  // so HasSources is the fail-closed nucleus for empty research nests.
  return crewFeedCardHasSources({
    secondary: ['sources'],
    sources: card.sources
  })
}

/**
 * Non-blocking Ask handoff notice for Ask-gated Go
 * (`propose_mutate`, or `ics` Ask chat seed when no calendar href).
 * Pass the resolved crewFeedGoHref — dead Ask (null href) → null notice
 * (anti-slop; do not invent Discuss theater). Calendar `ics` URLs
 * (non-chat href) skip the notice. Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewAskHandoffNotice(
  card: Pick<CrewFeedCard, 'primaryCta'>,
  resolvedHref: string | null
): { title: string; description: string } | null {
  const kind = card.primaryCta?.kind
  if (!isCrewAskGoKind(kind)) return null
  // ics with a real calendar URL/path is navigation — not Ask chat handoff.
  if (
    kind === 'ics' &&
    resolvedHref &&
    !resolvedHref.includes('/chat?query=')
  ) {
    return null
  }
  // Dead Ask Go — silence (hasGo already refuses; no Discuss invent).
  if (!resolvedHref?.trim()) return null
  const description =
    CREW_ASK_HANDOFF_DESCRIPTION || CREW_ASK_HANDOFF_FALLBACK
  if (!description.trim()) return null
  return {
    title: CREW_ASK_HANDOFF_TITLE,
    description
  }
}

/**
 * Accessible name for Ask handoff note chrome. Compose from notice title +
 * description — do not re-inline in card JSX. Null when notice is null.
 */
export function crewAskHandoffAriaLabel(
  notice: { title: string; description: string } | null | undefined
): string | null {
  if (!notice) return null
  const title = notice.title.trim()
  const description = notice.description.trim()
  if (!title && !description) return null
  if (!title) return description
  if (!description) return title
  return `${title}. ${description}`
}

/** Discuss seed prompt — invent framing silenced (anti-slop); seed only. */
export const CREW_FEED_DISCUSS_PROMPT_PREFIX = '' as const
/** Join after mate label — silenced (anti-slop); seed only. */
export const CREW_FEED_DISCUSS_PROMPT_FOUND_JOIN = '' as const

/**
 * Discuss prompt seed — single contract for web + mobile.
 * Prefer trimmed summary, then trimmed title. Empty seed → empty prompt
 * (fail-closed — do not invent "this find" theater). Framing invent
 * silenced (anti-slop) — return seed only (mate already on card chrome).
 * Keep this string identical in mobile/lib/crew/normalize.ts.
 */
export function buildCrewDiscussPrompt(card: Pick<
  CrewFeedCard,
  'mateLabel' | 'summary' | 'title'
>): string {
  void card.mateLabel
  const seed =
    (typeof card.summary === 'string' && card.summary.trim()) ||
    (typeof card.title === 'string' && card.title.trim()) ||
    CREW_FEED_DISCUSS_SEED_FALLBACK_FIND
  if (!seed) return ''
  return seed
}

/**
 * Ask (propose_mutate / ics) prompt — seeds chat with the proposed action.
 * Does not execute mutate; user/Ask / plan-approve flow decides.
 * Shared Ask Go fallback when primaryCta has no navigation href (and for
 * `ics`, no payload.url / path either — see crewFeedGoHref).
 * Empty mutate + empty context → '' (fail-closed; no "this change"/"this card").
 * Keep string shape identical in mobile/lib/crew/normalize.ts.
 */
export function buildCrewAskPrompt(card: CrewFeedCard): string {
  const payload = card.primaryCta?.payload ?? {}
  const draftRaw = payload.draft
  const draft =
    draftRaw && typeof draftRaw === 'object' && !Array.isArray(draftRaw)
      ? (draftRaw as Record<string, unknown>)
      : null

  const mutateCandidate =
    (typeof payload.mutate === 'string' && payload.mutate.trim()) ||
    (typeof draft?.mutate === 'string' && draft.mutate.trim()) ||
    CREW_ASK_MUTATE_FALLBACK
  if (!mutateCandidate) return ''

  // Ask seed = mutate (+ compact draft) only (anti-slop). No action/type
  // alias invent — producers must stamp payload.mutate (or draft.mutate).
  void CREW_ASK_CONTEXT_FALLBACK
  let prompt = mutateCandidate
  if (draft) {
    try {
      const compact = JSON.stringify(draft)
      if (compact.length > 0 && compact.length <= 400) {
        prompt += `. Draft: ${compact}`
      }
    } catch {
      // non-serializable draft — prompt still Ask-gates without it
    }
  }
  return prompt
}

/**
 * Whether a `/chat` href is a live Discuss target — conversation thread
 * (`/chat/<id>…`) or non-empty `?query=` seed. Bare `/chat`, `/chat/`,
 * empty `?query=`, or non-chat paths are dead (fall through to seed).
 * Nucleus: `isLivePulseChatHref` (pulse-card-target) — keep Go + Discuss shared.
 */
export function crewFeedDiscussChatTargetIsLive(href: string): boolean {
  return isLivePulseChatHref(href)
}

/**
 * Discuss href — prefer an explicit live discussHref (Spaces Pulse
 * conversation / seeded chat), else a live /chat Go (trim like
 * crewFeedGoHref), else seed a mate-aware discuss prompt via
 * buildCrewDiscussPrompt + buildChatQueryHref when the seed is non-empty.
 * Empty seed → '' (never bare `/chat` or "this find" theater).
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedDiscussHref(card: CrewFeedCard): string {
  // Spaces Pulse adapter may stash a conversation / chat discuss target.
  const discussHref = card.primaryCta?.payload.discussHref
  if (typeof discussHref === 'string' && discussHref.trim()) {
    const target = discussHref.trim()
    if (crewFeedDiscussChatTargetIsLive(target)) return target
  }
  const href =
    typeof card.primaryCta?.payload.href === 'string'
      ? card.primaryCta.payload.href.trim()
      : ''
  if (crewFeedDiscussChatTargetIsLive(href)) {
    return href
  }
  const prompt = buildCrewDiscussPrompt(card)
  if (!prompt.trim()) return ''
  // solo_seed only — mateLabel / mate_id = attribution; never group or room lists.
  // Fail closed if Discuss seed mode drifts (Crew Chat multi_mate is separate).
  if (CREW_FEED_DISCUSS_SEED_MODE !== 'solo_seed') {
    return ''
  }
  return buildChatQueryHref(prompt)
}

/**
 * Primary Go href from the card view model.
 * `propose_mutate` is Ask chat seed only — never navigate via payload
 * href/url/path (mutate stays plan-gated Ask). Navigation kinds: trimmed
 * `payload.href`, then (ics only) trimmed `url`/`path` for RFC 5545
 * HTTPS calendar feeds. `ics` without a calendar target falls back to
 * Ask chat seed (buildCrewAskPrompt) so Go is never a dead click when
 * hasGo is true. Handoff is chat Ask — not a separate Ask sheet.
 * Keep identical in mobile/lib/crew/normalize.ts.
 */
export function crewFeedGoHref(card: CrewFeedCard): string | null {
  const kind = card.primaryCta?.kind
  // propose_mutate Go is Ask seed only — ignore payload href/url/path.
  if (kind === 'propose_mutate') {
    const ask = buildCrewAskPrompt(card)
    if (!ask.trim()) return null
    return buildChatQueryHref(ask)
  }

  const payload = card.primaryCta?.payload
  const href = typeof payload?.href === 'string' ? payload.href.trim() : ''
  if (href) {
    // Bare `/chat` (no thread, no `?query=` seed) is dead — same live gate
    // as crewFeedCardHasGo / Discuss (do not invent a seed here).
    if (
      href.startsWith('/chat') &&
      !crewFeedDiscussChatTargetIsLive(href)
    ) {
      return null
    }
    // Invented `/nexus/crm/leads/:id` → list deep-link or refuse.
    return rewriteAbsentCrmLeadDetailHref(href)
  }

  // ics may stash the calendar HTTPS URL under url/path (buildPrimaryCta
  // already treats those as resolvable).
  if (kind === 'ics' && payload) {
    for (const key of ['url', 'path'] as const) {
      const value = payload[key]
      if (typeof value === 'string' && value.trim()) {
        return value.trim()
      }
    }
  }

  if (isCrewAskGoKind(kind)) {
    const ask = buildCrewAskPrompt(card)
    if (!ask.trim()) return null
    return buildChatQueryHref(ask)
  }
  return null
}

/**
 * Normalize a pulse_cards row into the shared CrewFeedCard view model.
 * Returns null when no resolvable Go exists (dead cards stay off the feed),
 * when mate why is empty after deriveWhy (§1e-5 slice 3), or when
 * research/interest claims sources without citations (§1e-5 slice 2).
 * Insight / memory_highlight are repaired before those gates.
 * `propose_mutate` stored CTAs keep the card without an href (Ask later).
 */
export function normalizePulseRow(row: PulseCardLike): CrewFeedCard | null {
  const laneFromRow =
    row.lane && isCrewFeedLane(row.lane)
      ? row.lane
      : mapCardType(row.card_type)

  const mate = resolveFeedMate(row.mate_id) ?? mateForLane(laneFromRow)

  const sources = parseSources(row)
  const primaryCta = buildPrimaryCta(row, laneFromRow)

  const secondary: CrewFeedCard['secondary'] = ['love', 'discuss']
  if (laneFromRow === 'follow_up') {
    secondary.push('save')
  }
  if (sources.length > 0) {
    secondary.push('sources')
  }

  const urgency = resolveCrewFeedUrgencyFromRow(row)

  const titleTrim =
    typeof row.title === 'string' ? row.title.trim() : ''
  const summaryTrim =
    typeof row.description === 'string' ? row.description.trim() : ''
  // Prefer real title; else reuse description as display title (anti-slop —
  // never invent "Untitled"; silence Untitled / "Untitled …" theater too).
  // Strip "Continue: …" first so "Continue: Untitled …" still fail-closes.
  const titleClean = crewFeedScrubDisplayTitle(titleTrim)
  const summaryClean = crewFeedScrubDisplayTitle(summaryTrim)
  const title = titleClean || summaryClean
  if (!title) return null

  const card: CrewFeedCard = {
    id: row.id,
    lane: laneFromRow,
    mateId: mate.id,
    mateLabel: mate.label,
    title,
    summary: summaryClean,
    why: deriveWhy(row),
    sources,
    primaryCta,
    secondary,
    expiresAt: row.expires_at ?? null,
    rawCardType: row.card_type ?? undefined,
    ...(urgency ? { urgency } : {})
  }

  // A card earns a slot when it points at a real destination OR explains
  // itself. For the actionable lanes (follow_up / ops), a resolvable Go to a
  // concrete target (Space item, CRM, planner, external) makes a why-less card
  // useful on its own — a title + a real place to go is not slop. Interest /
  // insight / calendar still require a producer why (watchlist / calendar
  // "theater" must be stamped by the producer). A /chat seed is never a real
  // destination. No qualifying Go AND no why → drop.
  const goHref = crewFeedGoHref(card)
  const hasRealDestinationGo = Boolean(goHref) && !goHref!.startsWith('/chat')
  const actionableLane = card.lane === 'follow_up' || card.lane === 'ops'
  const hasWhy = crewFeedCardHasWhy(card)
  if (!(actionableLane && hasRealDestinationGo)) {
    if (!hasWhy) {
      return null
    }
    if (!crewFeedCardHasGo(card)) {
      const discussOnlyLane =
        card.lane === 'insight' || card.lane === 'interest'
      if (!discussOnlyLane || !crewFeedCardHasDiscuss(card)) {
        return null
      }
    }
  }

  // §1e-5 slice 2: research/interest claims without HasSources → silence.
  if (
    !crewFeedPassesResearchSourcesOrSilenceGate(
      card,
      crewFeedRowClaimsResearchSources(row)
    )
  ) {
    return null
  }

  return card
}

// --------------
// Zod (shared contract — soft-parse at edges; runtime adapter stays above)
// --------------

export const CrewFeedLaneSchema = z.enum([
  'interest',
  'ops',
  'follow_up',
  'calendar',
  'insight'
])

export const CrewFeedSourceSchema = z.object({
  title: z.string().min(1),
  url: z.string().optional(),
  href: z.string().optional(),
  retrieved_at: z.string().optional()
})

export const CrewFeedPrimaryCtaSchema = z.object({
  icon: z.string().min(1),
  label: z.string().min(1),
  kind: z.enum([
    'deep_link',
    'open_chat',
    'ics',
    'open_url',
    'propose_mutate'
  ]),
  payload: z.record(z.unknown())
})

export const CrewFeedUrgencySchema = z.enum([
  'critical',
  'important',
  'fyi'
])

export const CrewFeedCardSchema = z.object({
  id: z.string().min(1),
  lane: CrewFeedLaneSchema,
  mateId: z.enum(['peta', 'jovi', 'etoile']),
  mateLabel: z.string().min(1),
  title: z.string(),
  summary: z.string(),
  // Trim so whitespace-only why fails closed (§1e-5 slice 3 — no Go without why).
  why: z.string().trim().min(1),
  sources: z.array(CrewFeedSourceSchema),
  primaryCta: CrewFeedPrimaryCtaSchema.nullable(),
  secondary: z.array(z.enum(['discuss', 'save', 'sources', 'love'])),
  expiresAt: z.string().nullable().optional(),
  rawCardType: z.string().optional(),
  urgency: CrewFeedUrgencySchema.optional()
})

/**
 * Soft-parse an unknown payload into CrewFeedCard at API edges.
 * Returns null on Zod failure — does not adapt pulse_cards rows
 * (use normalizePulseRow for that).
 */
export function parseCrewFeedCard(input: unknown): CrewFeedCard | null {
  const parsed = CrewFeedCardSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}

/**
 * Soft-parse a list of unknown payloads into CrewFeedCard[].
 * Drops invalid entries — API edges stay resilient.
 */
export function parseCrewFeedCardList(input: unknown): CrewFeedCard[] {
  if (!Array.isArray(input)) return []
  const cards: CrewFeedCard[] = []
  for (const item of input) {
    const card = parseCrewFeedCard(item)
    if (card) cards.push(card)
  }
  return cards
}
